import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { loadStripe } from '@stripe/stripe-js';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, ArrowRight, Shield, Rocket, Zap, Save, User, LogOut, History, Download, Code, Layout, Cloud, Server, Globe, Users, Target, Lightbulb, MapPin, Briefcase, BookOpen, ExternalLink, Loader2, MessageSquare, Coffee, Search, ThumbsDown, Sparkles, DollarSign, Building2, Laptop, Crown, X, CreditCard, FileText, Trash2 } from 'lucide-react';
import confetti from 'canvas-confetti';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import RoadmapVisualization from '../components/RoadmapVisualization';
import PaywallModal from '../components/PaywallModal';
import OrbitSystem from '../components/OrbitSystem';
import CareerNewsCarousel from '../components/CareerNewsCarousel';
import OnboardingAnswersEditor from '../components/OnboardingAnswersEditor';
import { generateCareerPaths } from '../utils/careerPathGenerator';
import { generateAdventurousPath, generateRiskyPath } from '../utils/roadmapGenerators';
import AuthModal from '../components/AuthModal';
import ReplaceResumeButton from '../components/ReplaceResumeButton';
import BookmarkFlyAnimation from '../components/BookmarkFlyAnimation';
import { useSavedRoles } from '../hooks/useSavedRoles';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ResumeProcessingAnimation from '../components/wrapped/ResumeProcessingAnimation';
import CoachTabContent from '../components/CoachTabContent';
import ShareableCard from '../components/ShareableCard';
import {
  trackRoadmapGenerated,
  trackPathSelected,
  trackRoadmapSaved,
  trackRoadmapEdited,
  trackPDFExport,
  trackButtonClick,
  trackSignOut,
  trackNavigation,
  trackError
} from '../utils/analytics';

const RoadmapPage = () => {
  const navigate = useNavigate();
  const { user, signOut, loading, hasLifetimeAccess, grantLifetimeAccess } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/connect-email');
    }
  }, [user, loading, navigate]);

  // Handle success redirect from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgrade = params.get('upgrade');
    const plan = params.get('plan');

    if (upgrade === 'success' && plan) {
      toast.success(`Successfully upgraded to ${plan === 'pro' ? 'Pro' : 'Base'} plan!`);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      setShowCheckoutSidebar(false);
      setCheckoutClientSecret(null);
    } else if (upgrade === 'cancelled') {
      toast.info('Checkout cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Handle lifetime payment return from Stripe (return_url = /roadmap?payment=success)
  //
  // Race condition fix: loadRoadmapFromSupabase fires concurrently and redirects
  // /roadmap → /roadmap/:id, which unmounts this component and kills the polling
  // interval before the webhook may have processed. We persist a sessionStorage flag
  // so the remounted component at /roadmap/:id picks up polling where it left off.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('payment') === 'success';
    const fromStorage = sessionStorage.getItem('figpay') === '1';

    if (!fromUrl && !fromStorage) return;

    // First mount (from Stripe redirect): persist the flag and clean the URL.
    // Subsequent mounts (after /roadmap → /roadmap/:id redirect): flag is already set.
    if (fromUrl) {
      sessionStorage.setItem('figpay', '1');
      window.history.replaceState({}, '', window.location.pathname);
    }

    setIsPollingPayment(true);
    let attempts = 0;

    const poll = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const res = await fetch(`${BACKEND_URL}/api/user/status`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.has_lifetime_access) {
            clearInterval(pollPaymentRef.current);
            setIsPollingPayment(false);
            sessionStorage.removeItem('figpay');
            grantLifetimeAccess();
            toast.success('Access unlocked! Welcome to your full roadmap.');
            return;
          }
        }
      } catch {
        // keep polling
      }
      attempts++;
      if (attempts >= 30) {
        // Stop polling for now but keep figpay in sessionStorage so the next
        // page load automatically restarts polling until access is confirmed.
        clearInterval(pollPaymentRef.current);
        setIsPollingPayment(false);
        toast.info('Still activating your access — refresh in a moment and it will unlock automatically.');
      }
    };

    poll();
    pollPaymentRef.current = setInterval(poll, 2000);
    return () => clearInterval(pollPaymentRef.current);
  }, []);
  // Safety net: if user is logged in but hasLifetimeAccess is still false,
  // re-check their access status. This catches existing paying customers whose
  // AuthContext status check failed on load (network hiccup, timeout, etc.).
  useEffect(() => {
    if (!user || hasLifetimeAccess) return;
    const isPollingFromPayment = sessionStorage.getItem('figpay') === '1';
    if (isPollingFromPayment) return; // payment polling useEffect handles this

    const check = async () => {
      try {
        // Try backend first
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch(`${BACKEND_URL}/api/user/status`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data.has_lifetime_access) {
            grantLifetimeAccess();
            return;
          }
        }
      } catch {
        // Backend failed — fall through to Supabase direct read
      }

      // Fallback: read directly from Supabase users table
      try {
        const { data, error } = await supabase
          .from('users')
          .select('has_lifetime_access')
          .eq('id', user.id)
          .single();
        if (!error && data?.has_lifetime_access === true) {
          grantLifetimeAccess();
        }
      } catch {
        // silent
      }
    };

    check();
  }, [user]);

  const { roadmapId } = useParams();
  const [formData, setFormData] = useState(null);
  const [careerPaths, setCareerPaths] = useState(null);
  const [noRoadmapFound, setNoRoadmapFound] = useState(false);
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('roadmap_activeTab') || 'stable');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const { savedRoles, saveRole, removeRole, isSaved: isRoleSaved } = useSavedRoles(user);
  const [bookmarkAnim, setBookmarkAnim] = useState({ show: false, x: 0, y: 0 });
  const [saving, setSaving] = useState(false);
  const [researching, setResearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [updatingAnswers, setUpdatingAnswers] = useState(false);
  const [currentRoadmapId, setCurrentRoadmapId] = useState(null);
  const [editInstruction, setEditInstruction] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isStableSet, setIsStableSet] = useState(() => sessionStorage.getItem('roadmap_isStableSet') === 'true'); // Track if user completed stable path
  const [showShareableCard, setShowShareableCard] = useState(false);
  const [stablePathNodes, setStablePathNodes] = useState(null);
  const [feedPreviewPosts, setFeedPreviewPosts] = useState([]);
  const spillTeaButtonRef = useRef(null);
  const spillTeaConfettiFired = useRef(false);
  const [showSpillTeaBlurb, setShowSpillTeaBlurb] = useState(false);
  const isEditingRef = useRef(false);
  const isGeneratingRef = useRef(false); // Prevents double-firing in Strict Mode
  const generationFailedRef = useRef(false); // Prevents infinite retry loop on API failure
  const hasLoadedFromSupabaseRef = useRef(false); // Prevents re-fetching from Supabase
  const pathTabsRef = useRef(null); // Reference to path tabs section for scrolling
  const shareableCardConfettiFired = useRef(false); // Track if shareable card confetti already played
  const [showCheckoutSidebar, setShowCheckoutSidebar] = useState(false); // Stripe checkout sidebar
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null); // Stripe client secret
  const [tsenta_applying, setTsentaApplying] = useState(false);
  const [tsentaSurveyVisible, setTsentaSurveyVisible] = useState(() =>
    !!localStorage.getItem('tsenta_applied_at') && !localStorage.getItem('tsenta_survey_done')
  );
  const [tsentaSurveySubmitted, setTsentaSurveySubmitted] = useState(false);
  const [stripePromise, setStripePromise] = useState(null); // Stripe instance
  const checkoutRef = useRef(null); // Ref for Stripe embedded checkout mount point
  const pricingTableRef = useRef(null); // Ref for Stripe Pricing Table

  // --- What-If State ---
  const [whatIfActiveNode, setWhatIfActiveNode] = useState(null); // Which node has popover open
  const [whatIfLoading, setWhatIfLoading] = useState(false);

  // --- Inline Paywall ---
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [isPollingPayment, setIsPollingPayment] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const pollPaymentRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  // Quick action instruction templates for refining roadmaps
  const quickActionInstructions = {
    moreCreative: "Emphasize creative roles across all paths - Design, Content Strategy, Creative Direction, Brand roles. Keep the progression realistic.",
    higherSalary: "Focus on higher-paying roles ($180k+ base). Adjust titles and companies to match compensation expectations.",
    startupFocus: "Change all roles to be at startups or high-growth companies. Include equity/options context.",
    bigTechFocus: "Focus all roles on FAANG and top-tier tech companies (Google, Meta, Apple, Amazon, Microsoft, Netflix, Stripe).",
    moreTechnical: "Make roles more technical/engineering focused. Include more hands-on coding and architecture work.",
    moreLeadership: "Shift toward management and leadership track. Include people management and strategic roles."
  };

  // Quick action chip configurations
  const quickActionChips = [
    { id: 'moreCreative', label: 'More Creative', icon: Sparkles },
    { id: 'higherSalary', label: 'Higher Salary', icon: DollarSign },
    { id: 'startupFocus', label: 'Startups', icon: Rocket },
    { id: 'bigTechFocus', label: 'Big Tech', icon: Building2 },
    { id: 'moreTechnical', label: 'More Technical', icon: Laptop },
    { id: 'moreLeadership', label: 'Leadership', icon: Crown }
  ];

  // Initialize Stripe on mount
  useEffect(() => {
    const initStripe = async () => {
      // You'll need to add REACT_APP_STRIPE_PUBLISHABLE_KEY to your frontend .env
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
      if (publishableKey) {
        const stripe = await loadStripe(publishableKey);
        setStripePromise(stripe);
      } else {
        console.warn('REACT_APP_STRIPE_PUBLISHABLE_KEY not set');
      }
    };
    initStripe();
  }, []);

  // Load Stripe Pricing Table script when sidebar opens
  useEffect(() => {
    if (!showCheckoutSidebar || checkoutClientSecret) return;

    // Check if Stripe Pricing Table script is already loaded
    if (window.Stripe && window.Stripe.pricingTable) {
      return;
    }

    // Load Stripe Pricing Table script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    script.onload = () => {
      // Script loaded, pricing table will render automatically via the <stripe-pricing-table> element
    };
    document.body.appendChild(script);

    return () => {
      // Cleanup: remove script if component unmounts
      const existingScript = document.querySelector('script[src="https://js.stripe.com/v3/pricing-table.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [showCheckoutSidebar, checkoutClientSecret]);

  // Initialize embedded checkout when client_secret is available
  useEffect(() => {
    if (!checkoutClientSecret || !stripePromise || !checkoutRef.current) return;

    let embeddedCheckout;

    const initCheckout = async () => {
      try {
        embeddedCheckout = await stripePromise.initEmbeddedCheckout({
          clientSecret: checkoutClientSecret,
        });
        embeddedCheckout.mount(checkoutRef.current);
      } catch (error) {
        console.error('Error initializing Stripe checkout:', error);
        toast.error('Failed to load checkout. Please try again.');
      }
    };

    initCheckout();

    // Cleanup on unmount
    return () => {
      if (embeddedCheckout) {
        embeddedCheckout.unmount();
      }
    };
  }, [checkoutClientSecret, stripePromise]);

  const handleUpgradeClick = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        // Track upgrade click for analytics
        await fetch(`${BACKEND_URL}/api/billing/track-upgrade-click`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        });
      }
    } catch (error) {
      console.error('Error tracking upgrade click:', error);
    }

    // Open sidebar - Stripe Pricing Table will show plans automatically
    setShowCheckoutSidebar(true);
  };

  const handleCloseSidebar = () => {
    setShowCheckoutSidebar(false);
    setCheckoutClientSecret(null);
  };

  const handleManageBilling = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setShowAuthModal(true);
        return;
      }

      const response = await fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(errorData.detail || 'Failed to create portal session');
      }

      const data = await response.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (error) {
      console.error('Portal error:', error);
      toast.error(error.message || 'Could not open billing portal. Please try again.');
    }
  };

  // --- Big attention-grabbing confetti burst from Spill Tea button on page load ---
  useEffect(() => {
    if (spillTeaConfettiFired.current) return;
    const timers = [];

    const timer = setTimeout(() => {
      if (!spillTeaButtonRef.current) return;
      spillTeaConfettiFired.current = true;

      const rect = spillTeaButtonRef.current.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = (rect.top + rect.height / 2) / window.innerHeight;

      const teaColors = ['#8B6914', '#5C3A1E', '#D4A574', '#FFD700', '#2563eb', '#60a5fa', '#E0BBE4', '#FEC8D8', '#BAE1FF', '#BAFFC9'];

      // === Wave 1: Big upward fountain (t=0) ===
      confetti({
        particleCount: 60,
        spread: 80,
        startVelocity: 45,
        gravity: 0.7,
        scalar: 1.1,
        origin: { x, y },
        colors: teaColors,
        ticks: 200,
        shapes: ['circle', 'square'],
      });

      // Left fan
      confetti({
        particleCount: 30,
        angle: 135,
        spread: 50,
        startVelocity: 35,
        gravity: 0.6,
        scalar: 0.9,
        origin: { x: x - 0.01, y: y + 0.01 },
        colors: teaColors,
        ticks: 180,
      });

      // Right fan
      confetti({
        particleCount: 30,
        angle: 45,
        spread: 50,
        startVelocity: 35,
        gravity: 0.6,
        scalar: 0.9,
        origin: { x: x + 0.01, y: y + 0.01 },
        colors: teaColors,
        ticks: 180,
      });

      // === Wave 2: Second burst (t=400ms) ===
      const t2 = setTimeout(() => {
        confetti({
          particleCount: 50,
          spread: 100,
          startVelocity: 40,
          gravity: 0.65,
          scalar: 1.0,
          origin: { x, y: y - 0.02 },
          colors: teaColors,
          ticks: 200,
        });
        confetti({
          particleCount: 20,
          angle: 60,
          spread: 40,
          startVelocity: 30,
          gravity: 0.7,
          scalar: 0.8,
          origin: { x: x - 0.04, y },
          colors: teaColors,
          ticks: 160,
        });
        confetti({
          particleCount: 20,
          angle: 120,
          spread: 40,
          startVelocity: 30,
          gravity: 0.7,
          scalar: 0.8,
          origin: { x: x + 0.04, y },
          colors: teaColors,
          ticks: 160,
        });
      }, 400);
      timers.push(t2);

      // === Wave 3: Final shower (t=900ms) ===
      const t3 = setTimeout(() => {
        confetti({
          particleCount: 40,
          spread: 120,
          startVelocity: 35,
          gravity: 0.5,
          scalar: 0.9,
          origin: { x, y: y - 0.03 },
          colors: teaColors,
          ticks: 220,
          drift: 0.5,
        });
        confetti({
          particleCount: 25,
          angle: 90,
          spread: 160,
          startVelocity: 25,
          gravity: 0.4,
          scalar: 1.2,
          origin: { x, y: 0 },
          colors: teaColors,
          ticks: 250,
          shapes: ['circle'],
        });
      }, 900);
      timers.push(t3);

      // Show the "NEW FEATURE" tooltip blurb
      setShowSpillTeaBlurb(true);
      const blurbTimer = setTimeout(() => setShowSpillTeaBlurb(false), 4500);
      timers.push(blurbTimer);
    }, 800);

    timers.push(timer);
    return () => timers.forEach(clearTimeout);
  }, []);

  // Fetch feed preview posts for the community window
  useEffect(() => {
    const fetchFeedPreview = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        const response = await fetch(`${BACKEND_URL}/api/feed/posts?sort=new&limit=3`, {
          headers: { Authorization: `Bearer ${session.access_token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setFeedPreviewPosts(data.posts || []);
        }
      } catch (error) {
      }
    };

    if (user) {
      fetchFeedPreview();
    }
  }, [user, BACKEND_URL]);


  // --- Helpers for Orbit System ---
  function getIconForLabel(label) {
    if (!label) return Briefcase;
    const l = label.toLowerCase();
    if (l.includes('python') || l.includes('code')) return Code;
    if (l.includes('react') || l.includes('front')) return Layout;
    if (l.includes('aws') || l.includes('cloud')) return Cloud;
    if (l.includes('node') || l.includes('back')) return Server;
    if (l.includes('design') || l.includes('ui')) return Globe;
    if (l.includes('lead') || l.includes('manage')) return Users;
    if (l.includes('product')) return Zap;
    if (l.includes('strat')) return Target;
    if (l.includes('vision') || l.includes('idea')) return Lightbulb;
    if (l.includes('loc') || l.includes('remote')) return MapPin;
    return Briefcase;
  }

  function getRandomStyle() {
    const styles = [
      { color: 'text-blue-500', bg: 'bg-blue-50' },
      { color: 'text-cyan-500', bg: 'bg-cyan-50' },
      { color: 'text-orange-500', bg: 'bg-orange-50' },
      { color: 'text-green-500', bg: 'bg-green-50' },
      { color: 'text-pink-500', bg: 'bg-pink-50' },
      { color: 'text-purple-600', bg: 'bg-purple-50' },
      { color: 'text-indigo-500', bg: 'bg-indigo-50' },
      { color: 'text-rose-500', bg: 'bg-rose-50' },
    ];
    return styles[Math.floor(Math.random() * styles.length)];
  }

  const getOrbitData = () => {
    // Wrap in try-catch to prevent freeze if anything goes wrong
    try {
      if (!formData) return { inner: [], outer: [] };

      // Helper functions defined first
      function isSimilarEnough(str1, str2) {
        const s1 = str1.toLowerCase();
        const s2 = str2.toLowerCase();
        if (s1 === s2) return true;
        if (s1.startsWith(s2) || s2.startsWith(s1)) return true;
        const minLen = Math.min(s1.length, s2.length);
        if (minLen >= 4 && s1.substring(0, 4) === s2.substring(0, 4)) return true;
        return false;
      }

      function getRelatedTerms(term) {
        const related = {
          'career': ['Path', 'Journey', 'Trajectory'],
          'growth': ['Progress', 'Development', 'Advancement'],
          'future': ['Ahead', 'Forward', 'Vision'],
          'tech': ['Technology', 'Innovation', 'Digital'],
          'design': ['Creative', 'Aesthetic', 'Visual'],
          'impact': ['Influence', 'Change', 'Contribution'],
          'leadership': ['Management', 'Strategy', 'Direction'],
          'product': ['Building', 'Shipping', 'Launching'],
          'engineering': ['Technical', 'Systems', 'Architecture'],
          'data': ['Analytics', 'Insights', 'Intelligence'],
          'marketing': ['Brand', 'Growth', 'Outreach'],
          'startup': ['Founder', 'Entrepreneurship', 'Venture'],
          'finance': ['Investment', 'Strategy', 'Planning'],
          'creative': ['Artistic', 'Innovative', 'Original'],
          'communication': ['Storytelling', 'Messaging', 'Clarity'],
          'learning': ['Education', 'Mastery', 'Curiosity'],
          'autonomy': ['Independence', 'Freedom', 'Self-Directed'],
          'collaboration': ['Teamwork', 'Partnership', 'Synergy'],
          'problem': ['Solutions', 'Challenges', 'Opportunities'],
          'strategy': ['Planning', 'Execution', 'Roadmap']
        };
        return related[term.toLowerCase()] || [];
      }

      // Curated, unique keywords that represent the individual
      let curatedTags = [];

      // PRIORITY 1: Values & Personality from Conversation (Most Unique)
      // Check both nested and top-level structures
      const extracted = formData.extracted_career_data || formData.conversation_data || {};

      // Values are highly unique to the individual - will be split into single words later
      if (Array.isArray(extracted.values)) {
        curatedTags.push(...extracted.values.filter(v => v && typeof v === 'string'));
      }
      // Career goals show unique aspirations - extract single words
      if (Array.isArray(extracted.career_goals)) {
        extracted.career_goals.forEach(goal => {
          if (typeof goal === 'string') {
            const words = goal.toLowerCase().split(/\s+/);
            // Extract meaningful single words (avoid common words)
            const meaningful = words.filter(w =>
              w.length > 3 &&
              !['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'from', 'becoming', 'being'].includes(w)
            );
            curatedTags.push(...meaningful.slice(0, 3).map(w => w.charAt(0).toUpperCase() + w.slice(1)));
          }
        });
      }
      // Dream jobs - extract single words from job titles
      if (Array.isArray(extracted.dream_jobs)) {
        extracted.dream_jobs.forEach(job => {
          if (typeof job === 'string') {
            const words = job.toLowerCase().split(/\s+/);
            const meaningful = words.filter(w =>
              w.length > 2 &&
              !['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'from'].includes(w)
            );
            curatedTags.push(...meaningful.slice(0, 2).map(w => w.charAt(0).toUpperCase() + w.slice(1)));
          }
        });
      }
      // Things to avoid reveal unique preferences
      if (Array.isArray(extracted.things_to_avoid)) {
        // Convert "avoid X" into positive keywords where possible
        extracted.things_to_avoid.forEach(avoid => {
          if (typeof avoid === 'string' && avoid.length > 0) {
            // Extract the opposite or key concept
            const words = avoid.toLowerCase().split(/\s+/);
            const keyWord = words.find(w => w.length > 4 && !['avoid', 'hate', 'don\'t', 'never', 'not'].includes(w));
            if (keyWord) {
              curatedTags.push(keyWord.charAt(0).toUpperCase() + keyWord.slice(1));
            }
          }
        });
      }

      // PRIORITY 2: Identity Profile (AI-Extracted Personality & Values)
      if (formData.identity_profile) {
        // Values are most unique
        if (Array.isArray(formData.identity_profile.values)) {
          curatedTags.push(...formData.identity_profile.values.filter(v => v && typeof v === 'string'));
        }
        // Core attributes reveal personality
        if (Array.isArray(formData.identity_profile.core_attributes)) {
          curatedTags.push(...formData.identity_profile.core_attributes.filter(a => a && typeof a === 'string'));
        }
      }

      // PRIORITY 3: Distinct Resume Attributes (What they're good at - key skills/achievements)
      if (formData.resume_data) {
        // Key technical skills (distinct, not generic)
        const techSkills = formData.resume_data.tech_skills || formData.resume_data.all_skills || formData.resume_data.skills || [];
        if (Array.isArray(techSkills)) {
          // Take most distinct/unique skills (avoid common ones)
          // LIMIT INPUT to avoid perf issues
          const distinctSkills = techSkills
            .slice(0, 30) // Safety cap
            .filter(s => s && typeof s === 'string' && s.length > 0)
            .filter(s => !['communication', 'teamwork', 'leadership', 'problem solving'].includes(s.toLowerCase()))
            .slice(0, 4);
          curatedTags.push(...distinctSkills);
        }

        // Industries (distinct domains) - will be split into single words later
        const industries = formData.resume_data.industries || formData.resume_data.industry;
        if (Array.isArray(industries)) {
          curatedTags.push(...industries.slice(0, 3));
        } else if (typeof industries === 'string' && industries.length > 0) {
          curatedTags.push(...industries.split(',').map(i => i.trim()).slice(0, 3));
        }
      }

      // PRIORITY 4: YouTube Genres (If uploaded)
      if (formData.resume_data && formData.resume_data.youtube) {
        const yt = formData.resume_data.youtube;
        // Recurring topics/genres from YouTube
        if (Array.isArray(yt.recurring_topics)) {
          curatedTags.push(...yt.recurring_topics.filter(t => t && typeof t === 'string').slice(0, 3));
        }
        // Interests from YouTube
        if (Array.isArray(yt.interests)) {
          curatedTags.push(...yt.interests.filter(i => i && typeof i === 'string').slice(0, 2));
        }
        // Hobbies from YouTube
        if (Array.isArray(yt.hobbies)) {
          curatedTags.push(...yt.hobbies.filter(h => h && typeof h === 'string').slice(0, 2));
        }
      }

      // PRIORITY 5: Conversation Interests & Personality (AI Chat Insights)
      if (Array.isArray(extracted.interests)) {
        curatedTags.push(...extracted.interests.filter(i => i && typeof i === 'string').slice(0, 4));
      }

      // Personality traits from conversation extraction
      if (Array.isArray(extracted.personality_traits)) {
        curatedTags.push(...extracted.personality_traits.filter(t => t && typeof t === 'string').slice(0, 3));
      }

      // Personality traits from identity_profile (fallback)
      if (formData.identity_profile && Array.isArray(formData.identity_profile.personality_traits)) {
        curatedTags.push(...formData.identity_profile.personality_traits.filter(t => t && typeof t === 'string').slice(0, 2));
      }

      // PRIORITY 6: Role Models - SKIP adding names directly
      // Role models are people's names, not skills/interests, so we don't add them

      // PRIORITY 7: Selected tags from onboarding (user's own words)
      if (Array.isArray(formData.selected_tags)) {
        curatedTags.push(...formData.selected_tags.filter(t => t && typeof t === 'string').slice(0, 3));
      }

      // Split all tags into single words and clean
      const singleWords = [];
      // Safety cap on total tags to process
      const safeTags = curatedTags.slice(0, 50);

      safeTags.forEach(tag => {
        if (typeof tag === 'string' && tag.trim().length > 0) {
          const trimmed = tag.trim();
          // Split by spaces, hyphens, or underscores to get individual words
          const words = trimmed.split(/[\s\-_]+/).filter(w => w.length > 0);
          words.forEach(word => {
            // Clean each word
            const cleaned = word
              .replace(/[^\w\s]/g, '') // Remove special characters
              .trim();
            if (cleaned.length >= 2 && cleaned.length <= 15) { // Single word length limit
              // Capitalize first letter
              const capitalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1).toLowerCase();
              singleWords.push(capitalized);
            }
          });
        }
      });

      // Blocklist: common names, filler words, and incomplete thoughts
      const blocklist = new Set([
        // Common filler/stop words
        'and', 'the', 'for', 'with', 'from', 'this', 'that', 'are', 'was', 'were',
        'been', 'have', 'has', 'had', 'will', 'would', 'could', 'should', 'may',
        'might', 'must', 'shall', 'can', 'need', 'want', 'like', 'just', 'also',
        'very', 'really', 'quite', 'much', 'more', 'most', 'some', 'any', 'all',
        'each', 'every', 'both', 'few', 'many', 'such', 'what', 'which', 'who',
        'whom', 'whose', 'where', 'when', 'why', 'how', 'than', 'then', 'now',
        'here', 'there', 'about', 'into', 'through', 'during', 'before', 'after',
        'above', 'below', 'between', 'under', 'again', 'further', 'once', 'only',
        'same', 'other', 'another', 'being', 'doing', 'having', 'getting', 'going',
        'making', 'taking', 'coming', 'seeing', 'knowing', 'thinking', 'looking',
        'wanting', 'using', 'finding', 'giving', 'telling', 'working', 'trying',
        'leaving', 'calling', 'keeping', 'letting', 'beginning', 'seeming',
        'helping', 'showing', 'hearing', 'playing', 'running', 'moving', 'living',
        'believing', 'bringing', 'happening', 'writing', 'providing', 'sitting',
        'standing', 'losing', 'paying', 'meeting', 'including', 'continuing',
        'setting', 'learning', 'changing', 'leading', 'understanding', 'watching',
        'following', 'stopping', 'creating', 'speaking', 'reading', 'allowing',
        'adding', 'spending', 'growing', 'opening', 'walking', 'winning', 'offering',
        'remembering', 'loving', 'considering', 'appearing', 'buying', 'waiting',
        'serving', 'dying', 'sending', 'expecting', 'building', 'staying', 'falling',
        // Common first names (expanded list)
        'james', 'john', 'robert', 'michael', 'david', 'william', 'richard', 'joseph',
        'thomas', 'charles', 'christopher', 'daniel', 'matthew', 'anthony', 'mark',
        'donald', 'steven', 'paul', 'andrew', 'joshua', 'kenneth', 'kevin', 'brian',
        'george', 'timothy', 'ronald', 'edward', 'jason', 'jeffrey', 'ryan', 'jacob',
        'gary', 'nicholas', 'eric', 'jonathan', 'stephen', 'larry', 'justin', 'scott',
        'brandon', 'benjamin', 'samuel', 'raymond', 'gregory', 'frank', 'alexander',
        'patrick', 'jack', 'dennis', 'jerry', 'tyler', 'aaron', 'jose', 'adam', 'nathan',
        'mary', 'patricia', 'jennifer', 'linda', 'elizabeth', 'barbara', 'susan',
        'jessica', 'sarah', 'karen', 'lisa', 'nancy', 'betty', 'margaret', 'sandra',
        'ashley', 'kimberly', 'emily', 'donna', 'michelle', 'dorothy', 'carol',
        'amanda', 'melissa', 'deborah', 'stephanie', 'rebecca', 'sharon', 'laura',
        'cynthia', 'kathleen', 'amy', 'angela', 'shirley', 'anna', 'brenda', 'pamela',
        'emma', 'nicole', 'helen', 'samantha', 'katherine', 'christine', 'debra',
        'rachel', 'carolyn', 'janet', 'catherine', 'maria', 'heather', 'diane', 'ruth',
        'julie', 'olivia', 'joyce', 'virginia', 'victoria', 'kelly', 'lauren', 'christina',
        'elon', 'steve', 'jeff', 'mark', 'bill', 'warren', 'tim', 'sundar', 'satya',
        'jensen', 'sam', 'reid', 'peter', 'larry', 'sergey', 'travis', 'brian', 'drew',
        'oprah', 'beyonce', 'taylor', 'kim', 'kanye', 'drake', 'rihanna', 'lebron',
        // Common last names
        'smith', 'johnson', 'williams', 'brown', 'jones', 'garcia', 'miller', 'davis',
        'rodriguez', 'martinez', 'hernandez', 'lopez', 'gonzalez', 'wilson', 'anderson',
        'thomas', 'taylor', 'moore', 'jackson', 'martin', 'lee', 'perez', 'thompson',
        'white', 'harris', 'sanchez', 'clark', 'ramirez', 'lewis', 'robinson', 'walker',
        'young', 'allen', 'king', 'wright', 'scott', 'torres', 'nguyen', 'hill', 'flores',
        'green', 'adams', 'nelson', 'baker', 'hall', 'rivera', 'campbell', 'mitchell',
        'carter', 'roberts', 'musk', 'jobs', 'gates', 'bezos', 'zuckerberg', 'cook',
        'pichai', 'nadella', 'altman', 'hoffman', 'thiel', 'chesky', 'kalanick', 'dorsey',
        // Incomplete/meaningless words
        'etc', 'stuff', 'things', 'something', 'anything', 'everything', 'nothing',
        'someone', 'anyone', 'everyone', 'nobody', 'somebody', 'anybody', 'everybody',
        'somehow', 'anyhow', 'somewhat', 'maybe', 'perhaps', 'probably', 'possibly',
        'definitely', 'certainly', 'usually', 'often', 'sometimes', 'always', 'never',
        'yeah', 'yes', 'yep', 'nope', 'okay', 'sure', 'right', 'well', 'actually',
        'basically', 'literally', 'honestly', 'seriously', 'really', 'totally', 'super',
        'kinda', 'sorta', 'gonna', 'wanna', 'gotta', 'lemme', 'dunno',
        // Generic/vague words
        'good', 'great', 'nice', 'best', 'better', 'bad', 'worse', 'worst', 'new',
        'old', 'big', 'small', 'large', 'little', 'long', 'short', 'high', 'low',
        'different', 'important', 'able', 'available', 'free', 'full', 'general',
        'particular', 'possible', 'real', 'special', 'true', 'whole', 'certain'
      ]);

      // Clean and deduplicate - ensure only single words
      // Limit to 60 words for O(N^2) safety
      const cleanedTags = singleWords
        .slice(0, 60)
        .filter(t => t && typeof t === 'string' && t.trim().length > 0)
        .map(t => t.trim())
        .filter(t => t.length >= 3 && t.length <= 15) // Single word length (min 3 chars)
        .filter(t => !blocklist.has(t.toLowerCase())) // Remove blocklisted words
        .filter(t => !/^\d+$/.test(t)) // Remove pure numbers
        .filter(t => !/^[A-Z][a-z]+$/.test(t) || t.length > 5); // Filter short capitalized words that look like names

      // Remove duplicates while preserving order
      const uniqueTags = Array.from(new Set(cleanedTags.map(t => t.toLowerCase())))
        .map(lower => {
          // Find original case version
          return cleanedTags.find(t => t.toLowerCase() === lower) || lower;
        });

      // Ensure distinctness - remove similar words (fast O(n) check per pair)
      const distinctTags = [];
      for (const tag of uniqueTags) {
        const isSimilar = distinctTags.some(existing => isSimilarEnough(tag, existing));
        if (!isSimilar) {
          distinctTags.push(tag);
        }
        // Early exit if we have enough tags (target is 25)
        if (distinctTags.length >= 30) break;
      }

      // Fallback with smart defaults based on available data
      let displayTags = distinctTags;
      if (displayTags.length === 0) {
        // Try to infer from available data
        if (formData.resume_data) {
          const industries = formData.resume_data.industries || formData.resume_data.industry;
          if (industries && (Array.isArray(industries) ? industries.length > 0 : industries)) {
            const industryWords = Array.isArray(industries) ? industries : industries.split(',');
            displayTags = industryWords.slice(0, 3).map(i => i.trim().split(/\s+/)[0]);
          }
        }
        // Ultimate fallback
        if (displayTags.length === 0) {
          displayTags = ['Career', 'Growth', 'Future'];
        }
      }

      // TARGET: Always exactly 25 items total across both rings
      const TARGET_TOTAL = 25;

      // Fill to reach 25 items using smart fallbacks based on user context
      let fillAttempts = 0;
      const maxFillAttempts = 50;

      // Contextual fallback terms based on what we know about the user
      const getContextualFallbacks = () => {
        const fallbacks = [];

        // Career-oriented terms
        const careerTerms = ['Growth', 'Impact', 'Leadership', 'Strategy', 'Innovation', 'Vision', 'Goals', 'Success', 'Progress', 'Achievement'];

        // Skill-oriented terms
        const skillTerms = ['Creative', 'Analytical', 'Technical', 'Strategic', 'Communication', 'Problem-Solving', 'Collaboration', 'Adaptability'];

        // Value-oriented terms
        const valueTerms = ['Balance', 'Purpose', 'Freedom', 'Stability', 'Challenge', 'Learning', 'Autonomy', 'Mastery'];

        // Industry/domain terms (infer from resume if possible)
        const domainTerms = ['Technology', 'Business', 'Design', 'Product', 'Engineering', 'Marketing', 'Finance', 'Operations'];

        // Personality/work style terms
        const styleTerms = ['Focused', 'Dynamic', 'Driven', 'Curious', 'Ambitious', 'Resourceful', 'Resilient', 'Innovative'];

        // Add terms based on what data we have
        if (formData.resume_data) {
          fallbacks.push(...careerTerms, ...skillTerms);
        }
        if (extracted.values?.length > 0 || formData.identity_profile?.values?.length > 0) {
          fallbacks.push(...valueTerms);
        }
        if (extracted.career_goals?.length > 0 || extracted.dream_jobs?.length > 0) {
          fallbacks.push(...domainTerms);
        }
        fallbacks.push(...styleTerms);

        return fallbacks;
      };

      const contextualFallbacks = getContextualFallbacks();
      let fallbackIndex = 0;

      while (displayTags.length < TARGET_TOTAL && fillAttempts < maxFillAttempts) {
        fillAttempts++;

        // First try related terms from existing tags
        if (displayTags.length > 0) {
          const randomTag = displayTags[Math.floor(Math.random() * displayTags.length)];
          const variations = getRelatedTerms(randomTag);
          const newVariation = variations.find(v => !displayTags.map(t => t.toLowerCase()).includes(v.toLowerCase()));
          if (newVariation) {
            displayTags.push(newVariation);
            continue;
          }
        }

        // Then use contextual fallbacks
        while (fallbackIndex < contextualFallbacks.length && displayTags.length < TARGET_TOTAL) {
          const fallback = contextualFallbacks[fallbackIndex];
          fallbackIndex++;
          if (!displayTags.map(t => t.toLowerCase()).includes(fallback.toLowerCase())) {
            displayTags.push(fallback);
            break;
          }
        }

        // If we've exhausted fallbacks, break to avoid infinite loop
        if (fallbackIndex >= contextualFallbacks.length && displayTags.length < TARGET_TOTAL) {
          // Ultimate fallback - generic professional terms
          const ultimateFallbacks = ['Excellence', 'Execution', 'Momentum', 'Potential', 'Opportunity', 'Network', 'Expertise', 'Results', 'Performance', 'Development'];
          for (const term of ultimateFallbacks) {
            if (!displayTags.map(t => t.toLowerCase()).includes(term.toLowerCase()) && displayTags.length < TARGET_TOTAL) {
              displayTags.push(term);
            }
          }
          break;
        }
      }

      // Ensure exactly 25 items (cap if over, which shouldn't happen but safety first)
      displayTags = displayTags.slice(0, TARGET_TOTAL);

      const inner = [];
      const outer = [];

      // Distribute: 8 items to Inner Ring, 17 to Outer Ring (25 total)
      // This balances the density since the outer ring has a larger circumference
      displayTags.forEach((item, i) => {
        const style = getRandomStyle();
        const node = {
          id: i,
          label: item,
          icon: getIconForLabel(item),
          ...style
        };

        // First 8 go to inner, rest to outer
        if (i < 8) inner.push(node);
        else outer.push(node);
      });

      return { inner, outer };
    } catch (error) {
      console.error('Error in getOrbitData:', error);
      // Return safe fallback to prevent freeze
      return { inner: [], outer: [] };
    }
  };

  const { inner: innerIcons, outer: outerIcons } = React.useMemo(() => getOrbitData(), [formData]);

  useEffect(() => {
    // Skip if we've already loaded and have data (prevents reload loops)
    if (hasLoadedFromSupabaseRef.current && formData && careerPaths) {
      return;
    }

    // Check if we have a roadmap ID in the URL (prefer params, fallback to manual parse)
    // IMPORTANT: Prioritize the ID from the route params if available.
    const routeRoadmapId = roadmapId; // from useParams

    // Try to load roadmap from Supabase (latest or by ID)
    const loadRoadmapFromSupabase = async () => {
      try {
        let token = null;
        try {
          const { data: { session } } = await supabase.auth.getSession();
          token = session?.access_token;
        } catch (authErr) {
          console.warn('Auth session unavailable, continuing without token:', authErr.message);
        }

        const headers = token
          ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          : { 'Content-Type': 'application/json' };

        let roadmapData;

        // CASE 1: Specific Roadmap ID requested via URL
        if (routeRoadmapId && routeRoadmapId !== 'roadmap') {
          const endpoint = token
            ? `${BACKEND_URL}/api/roadmaps/${routeRoadmapId}`
            : `${BACKEND_URL}/api/roadmaps/dev-latest`;
          const response = await fetch(endpoint, { headers });

          if (response.ok) {
            const result = await response.json();
            roadmapData = result.roadmap;
          } else if (response.status === 404) {
            console.error(`❌ Roadmap ${routeRoadmapId} not found.`);
            toast.error("Roadmap not found");
            return false;
          }
        }
        // CASE 2: No specific ID, try to load the latest
        else {
          const endpoint = token
            ? `${BACKEND_URL}/api/roadmaps/latest`
            : `${BACKEND_URL}/api/roadmaps/dev-latest`;
          const response = await fetch(endpoint, { headers });
          if (response.ok) {
            const result = await response.json();
            roadmapData = result.roadmap;

            // Only redirect to specific URL if we have auth (avoids redirect loop in dev mode)
            if (token && roadmapData?.id) {
              navigate(`/roadmap/${roadmapData.id}`, { replace: true });
              return true; // Signal handled
            }
          } else if (response.status === 404) {
            return false;
          }
        }

        if (roadmapData) {
          // If we just redirected (latest -> specific ID), don't process further
          if (token && !routeRoadmapId && roadmapData.id) {
            return true;
          }

          setFormData(roadmapData.form_data);
          setCurrentRoadmapId(roadmapData.id);

          // Check if we have valid career paths (must have at least one valid path type)
          const hasValidPaths = roadmapData.career_paths &&
            (roadmapData.career_paths.stable ||
              roadmapData.career_paths.adventurous ||
              roadmapData.career_paths.risky);

          if (hasValidPaths) {
            hasLoadedFromSupabaseRef.current = true; // Mark as loaded
            setCareerPaths(roadmapData.career_paths);
            setResearching(false);

            // Check if stable path was already completed (has 4 nodes in selected_path)
            const stablePath = roadmapData.career_paths?.stable;
            const stableComplete = stablePath?.locked === true; // Check locked status
            if (stableComplete) {
              setIsStableSet(true);
              sessionStorage.setItem('roadmap_isStableSet', 'true');
              setStablePathNodes(stablePath.selected_path);
              // Show shareable card for completed paths (persists on return visits)
              setShowShareableCard(true);
            }

            // Note: Scroll to top is handled by the dedicated useEffect with hasScrolledRef
          } else {

            // CRITICAL: Only generate if we have chat context. If not, force user back to FigChat.
            const hasContext = roadmapData.form_data?.conversation?.length > 0 || roadmapData.form_data?.source === 'open_poke_chat';

            if (!hasContext) {
              navigate('/fig-chat', {
                state: {
                  forceChat: true,
                  resumeData: roadmapData.form_data?.resume_data,
                  scrapedProfile: roadmapData.form_data?.world_model ? { world_model: roadmapData.form_data.world_model } : null
                },
                replace: true
              });
              return true;
            }


            // Prevent auto-generation loops:
            if (!researching && roadmapData.form_data) {
              // Pass the known ID explicitly to ensure we update the existing record
              generateCareerPathsWithAI(roadmapData.form_data, roadmapData.id);
            }
          }
          return true;
        }
      } catch (error) {
        console.error('⚠️ Could not load roadmap from Supabase:', error.message, error.stack);
      }
      return false;
    };

    // Try Supabase first - if no roadmap found, show a "create new" screen
    loadRoadmapFromSupabase().then((result) => {
      if (result === false) {
        sessionStorage.removeItem('roadmap_isStableSet');
        sessionStorage.removeItem('roadmap_activeTab');
        setNoRoadmapFound(true);
      }
    });
  }, [navigate, roadmapId]);

  // Track if this is the initial load (only scroll on first load, not updates)
  const hasScrolledRef = useRef(false);

  // Scroll to top ONLY on initial career paths load, not on updates
  useEffect(() => {
    if (careerPaths && !researching && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  }, [careerPaths, researching]);

  // Small confetti burst when user manually scrolls to the shareable card section
  useEffect(() => {
    if (!showShareableCard) {
      shareableCardConfettiFired.current = false; // Reset when card hides
      return;
    }

    const section = document.getElementById('shareable-card-section');
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !shareableCardConfettiFired.current) {
          shareableCardConfettiFired.current = true;

          const pastelColors = ['#FFB3BA', '#FFDFBA', '#FFFFBA', '#BAFFC9', '#BAE1FF', '#E0BBE4'];

          // Single small confetti burst
          confetti({
            particleCount: 35,
            spread: 60,
            startVelocity: 20,
            gravity: 0.8,
            scalar: 0.7,
            origin: { x: 0.5, y: 0.6 },
            colors: pastelColors,
            ticks: 80,
          });
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, [showShareableCard]);

  const generateCareerPathsWithAI = async (data, existingRoadmapId = null) => {
    // Prevent multiple parallel generations (Strict Mode protection)
    if (researching || isGeneratingRef.current) {
      return;
    }

    // Use passed ID or fallback to state (state might be stale inside useEffect)
    const targetRoadmapId = existingRoadmapId || currentRoadmapId;

    isGeneratingRef.current = true;
    generationFailedRef.current = false; // Reset failure flag on each new attempt
    setResearching(true);
    setCareerPaths(null); // Clear existing paths

    // Accumulator for final save
    let finalPaths = { stable: null, adventurous: null, risky: null, identity_profile: null };

    try {
      // Use the new format that matches backend expectations
      const requestBody = {
        resume_data: data.resume_data || null,
        conversation: data.conversation || [],
        world_model: data.world_model || null, // Pass World Model to Backend
        country: data.country || 'United States',
        // Also include legacy format for backward compatibility
        role_models: data.roleModels || data.role_models || '',
        interests: data.interests || '',
        skills: data.skills || ''
      };

      // Use fetch for standard JSON response (Backend is not streaming)
      const { data: { session: _genSession } } = await supabase.auth.getSession();
      const response = await fetch(`${BACKEND_URL}/api/generate-career-path`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${_genSession?.access_token}`,
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) throw new Error(response.statusText);

      const responseData = await response.json();

      // Initialize with empty object to dismiss full screen loader
      setCareerPaths({});

      // Process the response data
      if (responseData) {
        // Handle Identity Profile if present
        Object.keys(responseData).forEach(key => {
          if (key === 'identity_profile') {
            setFormData(prev => ({
              ...prev,
              identity_profile: responseData[key]
            }));
            finalPaths.identity_profile = responseData[key];
          } else if (['stable', 'adventurous', 'risky'].includes(key)) {
            setCareerPaths(prev => ({
              ...(prev || {}),
              [key]: responseData[key]
            }));
            finalPaths[key] = responseData[key];
          }
        });
      }

      hasLoadedFromSupabaseRef.current = true; // Mark as loaded after generation

      // Note: Scroll to top is handled by the dedicated useEffect with hasScrolledRef

      // Auto-save generated paths
      try {
        if (targetRoadmapId) {

          // Use backend endpoint for reliable saving and logging
          const { data: { session } } = await supabase.auth.getSession();
          const token = session?.access_token;

          if (token) {
            const saveResponse = await fetch(`${BACKEND_URL}/api/update-roadmap-paths`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                roadmap_id: targetRoadmapId,
                career_paths: finalPaths,
                form_data: data
              })
            });

            if (saveResponse.ok) {
            } else {
              console.error("❌ Backend auto-save failed:", await saveResponse.text());
              // Fallback to client-side if backend fails (redundancy)
              await supabase
                .from('career_roadmaps')
                .update({ career_paths: finalPaths })
                .eq('id', targetRoadmapId);
            }
          }
        } else if (user) {
          // New Roadmap Creation

          const { data: newRoadmap, error } = await supabase
            .from('career_roadmaps')
            .insert([{
              user_id: user.id,
              title: `Career Roadmap - ${new Date().toLocaleDateString()}`,
              form_data: data,
              career_paths: finalPaths,
              role_model_research: null,
              non_linear_research: null
            }])
            .select()
            .single();

          if (error) {
            console.error("❌ Failed to create new roadmap:", error);
            throw error;
          }

          if (newRoadmap) {
            setCurrentRoadmapId(newRoadmap.id);
            // Important: Update URL without reloading
            window.history.replaceState(null, '', `/roadmap/${newRoadmap.id}`);

            // Update user onboarding status
            await supabase.from("users").update({ "has_done_onboarding": true }).eq("id", user.id);
          }
        }
      } catch (err) {
        console.error("Failed to auto-save paths:", err);
      }
    } catch (error) {
      console.error('Error generating career paths:', error);
      toast.error('Failed to generate career paths. Please refresh and try again.');
      generationFailedRef.current = true;
    } finally {
      setResearching(false);
      isGeneratingRef.current = false;
    }
  };

  const handleSaveRoadmap = async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    setSaving(true);
    try {
      let result;

      if (currentRoadmapId) {
        // Update existing
        result = await supabase
          .from('career_roadmaps')
          .update({
            form_data: formData,
            career_paths: careerPaths,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentRoadmapId)
          .select();
      } else {
        // Insert new
        result = await supabase
          .from('career_roadmaps')
          .insert([{
            user_id: user.id,
            title: `Career Roadmap - ${new Date().toLocaleDateString()}`,
            form_data: formData,
            career_paths: careerPaths,
            role_model_research: null,
            non_linear_research: null
          }])
          .select();
      }

      if (result.error) throw result.error;

      if (result.data && result.data[0]) {
        setCurrentRoadmapId(result.data[0].id);
        // localStorage.setItem('currentRoadmapId', result.data[0].id);
        // Track save event
        trackRoadmapSaved(result.data[0].id, !currentRoadmapId);
      }

      toast.success('Roadmap saved successfully!');
    } catch (error) {
      console.error('Error saving roadmap:', error);
      toast.error('Failed to save roadmap');
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateWithAI = async () => {
    if (!formData) return;

    // Clear all cached data
    localStorage.removeItem('savedCareerPaths');

    // Regenerate with fresh AI call
    await generateCareerPathsWithAI(formData);
  };

  const handleEditRoadmap = async () => {
    if (!editInstruction.trim()) return;
    setIsEditing(true);
    // Track edit attempt
    trackRoadmapEdited(editInstruction);
    try {
      const { data: { session: _editSession1 } } = await supabase.auth.getSession();
      const response = await axios.post(`${BACKEND_URL}/api/edit-roadmap`, {
        roadmap_id: currentRoadmapId,
        current_paths: careerPaths,
        instruction: editInstruction,
        user_profile: formData
      }, { timeout: 200000, headers: { Authorization: `Bearer ${_editSession1?.access_token}` } });

      if (response.data) {
        setCareerPaths(response.data);
        toast.success("Roadmap updated based on your feedback!");
        setEditInstruction('');

        // Update local storage to persist changes
        // localStorage.setItem('savedCareerPaths', JSON.stringify(response.data));
      }
    } catch (error) {
      console.error("Error editing roadmap:", error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 504 || error?.code === 'ECONNABORTED') {
        toast.error(detail || "The AI took too long. Please try a simpler edit.");
      } else {
        toast.error(detail || "Failed to update roadmap. Please try again.");
      }
    } finally {
      setIsEditing(false);
    }
  };

  // Handle quick refinement actions (chips)
  const handleQuickRefine = async (actionType) => {
    const instruction = quickActionInstructions[actionType];
    if (!instruction) return;

    setIsEditing(true);
    trackRoadmapEdited(`quick_action:${actionType}`);

    try {
      const { data: { session: _editSession2 } } = await supabase.auth.getSession();
      const response = await axios.post(`${BACKEND_URL}/api/edit-roadmap`, {
        roadmap_id: currentRoadmapId,
        current_paths: careerPaths,
        instruction: instruction,
        user_profile: formData
      }, { timeout: 200000, headers: { Authorization: `Bearer ${_editSession2?.access_token}` } });

      if (response.data) {
        setCareerPaths(response.data);
        toast.success("Roadmap updated!");
      }
    } catch (error) {
      console.error("Error refining roadmap:", error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 504 || error?.code === 'ECONNABORTED') {
        toast.error(detail || "The AI took too long. Please try a simpler edit.");
      } else {
        toast.error(detail || "Failed to update roadmap. Please try again.");
      }
    } finally {
      setIsEditing(false);
    }
  };

  // Handle disliking a specific role
  const handleDislikeRole = async (node, riskLevel) => {
    const instruction = `In the ${riskLevel} path, replace the ${node.year} "${node.title}" role with a completely different role that still fits my background but explores a different career direction. Keep the same year and risk level.`;

    setIsEditing(true);
    trackRoadmapEdited(`dislike_role:${node.title}`);

    try {
      const { data: { session: _editSession3 } } = await supabase.auth.getSession();
      const response = await axios.post(`${BACKEND_URL}/api/edit-roadmap`, {
        roadmap_id: currentRoadmapId,
        current_paths: careerPaths,
        instruction: instruction,
        user_profile: formData
      }, { timeout: 200000, headers: { Authorization: `Bearer ${_editSession3?.access_token}` } });

      if (response.data) {
        setCareerPaths(response.data);
        toast.success(`Found a new role for ${node.year}!`);
      }
    } catch (error) {
      console.error("Error replacing role:", error);
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 504 || error?.code === 'ECONNABORTED') {
        toast.error(detail || "The AI took too long. Please try a simpler edit.");
      } else {
        toast.error(detail || "Failed to replace role. Please try again.");
      }
    } finally {
      setIsEditing(false);
    }
  };

  // --- What-If Handlers ---
  const handleWhatIfDrop = (nodeIndex) => {
    // Open the popover for this node
    setWhatIfActiveNode(nodeIndex);
  };

  const handleWhatIfSubmit = async (nodeIndex, question) => {
    const pathType = activeTab;
    const pathData = careerPaths?.[pathType];
    if (!pathData) return;

    // Use selected_path for stable, nodes for others
    const currentNodes = pathType === 'stable'
      ? (pathData.selected_path || pathData.nodes || [])
      : (pathData.nodes || []);

    if (nodeIndex < 0 || nodeIndex >= currentNodes.length) {
      toast.error("Invalid step selected");
      return;
    }

    setWhatIfLoading(true);

    try {
      const { data: { session: _whatIfSession } } = await supabase.auth.getSession();
      const response = await axios.post(`${BACKEND_URL}/api/what-if-edit`, {
        roadmap_id: currentRoadmapId,
        path_type: pathType,
        start_index: nodeIndex,
        question: question,
        current_nodes: currentNodes,
        user_context: formData
      }, { headers: { Authorization: `Bearer ${_whatIfSession?.access_token}` } });

      if (response.data) {
        const { start_index, overridden_nodes, question: q } = response.data;

        // Build the override object
        const override = {
          question: q,
          originalNodes: currentNodes.slice(start_index),
          overriddenNodes: overridden_nodes,
          startIndex: start_index,
          createdAt: new Date().toISOString()
        };

        // Store in careerPaths
        const updatedPath = {
          ...pathData,
          whatIfOverrides: {
            [String(start_index)]: override
          }
        };

        const newPaths = { ...careerPaths, [pathType]: updatedPath };
        setCareerPaths(newPaths);

        // Persist to Supabase
        const targetId = roadmapId || currentRoadmapId;
        if (targetId) {
          try {
            await supabase
              .from('career_roadmaps')
              .update({
                career_paths: newPaths,
                updated_at: new Date().toISOString()
              })
              .eq('id', targetId);
          } catch (err) {
            console.error("Failed to save what-if override:", err);
          }
        }

        toast.success("What-If scenario generated!");
        setWhatIfActiveNode(null); // Close popover
      }
    } catch (error) {
      console.error("Error generating what-if scenario:", error);
      toast.error("Failed to generate scenario. Please try again.");
    } finally {
      setWhatIfLoading(false);
    }
  };

  const handleWhatIfReset = async (pathType) => {
    const pathData = careerPaths?.[pathType];
    if (!pathData) return;

    // Clear overrides
    const updatedPath = { ...pathData };
    delete updatedPath.whatIfOverrides;

    const newPaths = { ...careerPaths, [pathType]: updatedPath };
    setCareerPaths(newPaths);

    // Persist to Supabase
    const targetId = roadmapId || currentRoadmapId;
    if (targetId) {
      try {
        await supabase
          .from('career_roadmaps')
          .update({
            career_paths: newPaths,
            updated_at: new Date().toISOString()
          })
          .eq('id', targetId);
      } catch (err) {
        console.error("Failed to revert what-if override:", err);
      }
    }

    toast.info("Reverted to original roadmap");
  };

  const handleWhatIfClose = () => {
    setWhatIfActiveNode(null);
  };

  const handleUpdateAnswers = async (newAnswers) => {
    setUpdatingAnswers(true);
    try {
      // Update formData with new answers
      const updatedFormData = {
        ...formData,
        conversation: newAnswers
      };

      setFormData(updatedFormData);

      // Update localStorage
      // localStorage.setItem('careerFormData', JSON.stringify(updatedFormData));

      // Regenerate career paths and profile insights

      // Clear existing paths to trigger regeneration
      localStorage.removeItem('savedCareerPaths');
      setCareerPaths(null);

      // Regenerate paths
      await generateCareerPathsWithAI(updatedFormData);

      toast.success('Your career paths and profile have been updated!');
    } catch (error) {
      console.error('Error updating answers:', error);
      toast.error('Failed to update. Please try again.');
    } finally {
      setUpdatingAnswers(false);
    }
  };

  // ── Saved Roles (bookmarks) ──────────────────────────────────────────────
  const handleSaveRole = (node, event) => {
    const cleanTitle = node.title ? node.title.split(/ at /i)[0].trim() : node.title;
    const roleData = { ...node, title: cleanTitle };
    if (isRoleSaved(cleanTitle)) {
      removeRole(cleanTitle);
    } else {
      saveRole(roleData);
      const rect = event?.currentTarget?.getBoundingClientRect?.();
      const x = rect ? rect.left + rect.width / 2 : event?.clientX ?? window.innerWidth / 2;
      const y = rect ? rect.top : event?.clientY ?? 100;
      setBookmarkAnim({ show: true, x, y });
    }
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleApplyWithTsenta = async (node) => {
    if (tsenta_applying) return;
    if (!user?.email) { toast.error("Sign in to apply"); return; }
    setTsentaApplying(true);
    try {
      const session = await supabase.auth.getSession();
      const token = session?.data?.session?.access_token;
      const fullName = user.user_metadata?.full_name || user.email.split('@')[0];
      const roleTitle = node.title ? node.title.split(/ at /i)[0].trim() : '';
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000'}/api/apply-with-tsenta`,
        { email: user.email, full_name: fullName, role_title: roleTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { magic_link_url, resume_processing } = res.data;
      localStorage.setItem('tsenta_applied_at', Date.now().toString());
      localStorage.setItem('tsenta_applied_role', roleTitle);

      if (resume_processing) {
        toast.success("Your resume is being loaded into Tsenta — you'll land ready to go.", { duration: 4000 });
      } else {
        toast("Heads up: you may be asked to upload your resume again on Tsenta.", { duration: 4000 });
      }

      window.open(magic_link_url, '_blank', 'noopener,noreferrer');
      if (!localStorage.getItem('tsenta_survey_done')) {
        setTimeout(() => setTsentaSurveyVisible(true), 3000);
      }
    } catch (err) {
      toast.error("Couldn't connect to Tsenta — try again");
    } finally {
      setTsentaApplying(false);
    }
  };

  const handleTsentaSurvey = async (rating) => {
    setTsentaSurveySubmitted(true);
    localStorage.setItem('tsenta_survey_done', 'true');
    try {
      await supabase.from('partner_feedback').insert({
        user_id: user?.id || null,
        partner: 'tsenta',
        rating,
        role_title: localStorage.getItem('tsenta_applied_role') || null,
      });
    } catch (e) {
      // non-fatal
    }
    setTimeout(() => setTsentaSurveyVisible(false), 2000);
  };

  const handleSignOut = async () => {
    trackSignOut();
    await signOut();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${BACKEND_URL}/api/user/delete-account`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Deletion failed');

      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error('Failed to delete account. Please try again.');
      setIsDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleExportPDF = async () => {
    trackPDFExport();
    setExporting(true);

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;

      // Add title page
      pdf.setFontSize(24);
      pdf.text('Fig Careers', pageWidth / 2, 40, { align: 'center' });
      pdf.setFontSize(16);
      pdf.text('Your Career Roadmaps', pageWidth / 2, 55, { align: 'center' });

      // Add user profile
      pdf.setFontSize(10);
      pdf.text(`Skills: ${formData.skills}`, margin, 80);
      pdf.text(`Interests: ${formData.interests}`, margin, 90);
      if (formData.roleModels) {
        pdf.text(`Role Models: ${formData.roleModels}`, margin, 100);
      }

      // For each career path
      const riskLevels = ['stable', 'adventurous', 'risky'];
      const levelNames = ['Stable Path', 'Adventurous Path', 'Risky Path'];

      for (let i = 0; i < riskLevels.length; i++) {
        const level = riskLevels[i];
        const levelName = levelNames[i];
        const path = careerPaths[level];

        // Add new page for each path
        pdf.addPage();

        // Add path title
        pdf.setFontSize(18);
        pdf.text(levelName, margin, 20);

        pdf.setFontSize(10);
        let yPosition = 35;

        // Add each node
        path.nodes.forEach((node, idx) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 60) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(12);
          pdf.setFont(undefined, 'bold');
          pdf.text(`${node.year} - ${node.title}`, margin, yPosition);
          yPosition += 7;

          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');
          const descLines = pdf.splitTextToSize(node.description, pageWidth - 2 * margin);
          pdf.text(descLines, margin, yPosition);
          yPosition += descLines.length * 5;

          // Add actions
          if (node.details?.actions) {
            pdf.setFont(undefined, 'bold');
            pdf.text('Key Actions:', margin, yPosition);
            yPosition += 5;
            pdf.setFont(undefined, 'normal');
            node.details.actions.forEach((action) => {
              const actionLines = pdf.splitTextToSize(`• ${action}`, pageWidth - 2 * margin - 5);
              pdf.text(actionLines, margin + 5, yPosition);
              yPosition += actionLines.length * 4;
            });
            yPosition += 2;
          }

          // Add skills
          if (node.details?.skills) {
            pdf.setFont(undefined, 'bold');
            pdf.text('Skills:', margin, yPosition);
            yPosition += 5;
            pdf.setFont(undefined, 'normal');
            pdf.text(node.details.skills.join(', '), margin + 5, yPosition);
            yPosition += 7;
          }

          yPosition += 5; // Space between nodes
        });

        // Add sources if available
        if (path.sources && path.sources.length > 0) {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = 20;
          }

          pdf.setFontSize(11);
          pdf.setFont(undefined, 'bold');
          pdf.text('Research Sources:', margin, yPosition);
          yPosition += 7;

          pdf.setFontSize(8);
          pdf.setFont(undefined, 'normal');
          path.sources.slice(0, 5).forEach((source) => {
            const sourceLines = pdf.splitTextToSize(source, pageWidth - 2 * margin);
            pdf.text(sourceLines, margin, yPosition);
            yPosition += sourceLines.length * 4;
          });
        }
      }

      // Save the PDF via blob URL (works in Chrome where pdf.save() can be blocked)
      const fileName = `fig-careers-roadmap-${new Date().getTime()}.pdf`;
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  // No roadmap in the database — user deleted it or never created one
  if (noRoadmapFound) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0d0d0d', color: '#fff', gap: 20, padding: 32, textAlign: 'center' }}>
        <p style={{ fontSize: 22, fontWeight: 700 }}>You don't have a roadmap yet.</p>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', maxWidth: 400 }}>
          Looks like you deleted your previous roadmap. Start a new one by chatting with Fig — it only takes a few minutes.
        </p>
        <button
          onClick={() => navigate('/fig-chat', { state: { forceChat: true } })}
          style={{ marginTop: 8, padding: '14px 36px', borderRadius: 100, background: '#fff', color: '#0d0d0d', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}
        >
          Create New Roadmap
        </button>
      </div>
    );
  }

  // Show loading only if we don't have formData (required for page)
  if (!formData && researching) {
    return <ResumeProcessingAnimation isActive={true} />;
  }

  if (!formData) {
    return <ResumeProcessingAnimation isActive={true} />;
  }

  // If no career paths yet, show dashboard with loading state for paths (or pending message)
  if (!careerPaths) {
    if (researching) {
      return <ResumeProcessingAnimation isActive={true} />;
    }

    // Generation previously failed — show a retry screen instead of looping
    if (generationFailedRef.current) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0d0d0d', color: '#fff', gap: 20, padding: 32, textAlign: 'center' }}>
          <p style={{ fontSize: 22, fontWeight: 700 }}>Something went wrong generating your roadmap.</p>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', maxWidth: 400 }}>This is usually a temporary issue. Please try again.</p>
          <button
            onClick={() => { generationFailedRef.current = false; generateCareerPathsWithAI(formData, currentRoadmapId); }}
            style={{ marginTop: 8, padding: '14px 36px', borderRadius: 100, background: '#fff', color: '#0d0d0d', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}
          >
            Try Again
          </button>
        </div>
      );
    }

    // If we have formData but no paths and not researching, trigger generation once
    if (formData && !isGeneratingRef.current) {
      generateCareerPathsWithAI(formData, currentRoadmapId);
    }

    // Show loading state while generation starts
    return <ResumeProcessingAnimation isActive={true} />;
  }

  const riskLevels = [
    {
      id: 'stable',
      label: 'Stable Path',
      icon: Shield,
      color: '#16a34a', // Darker Green for Light Mode
      softColor: '#16a34a',
      description: 'Proven trajectory with predictable growth and lower risk',
      bgGradient: 'linear-gradient(135deg, rgba(255, 250, 245, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
      borderColor: '#16a34a'
    },
    {
      id: 'adventurous',
      label: 'Adventurous Path',
      icon: Rocket,
      color: '#ea580c', // Darker Orange for Light Mode
      softColor: '#ea580c',
      description: 'Balanced approach mixing traditional and innovative opportunities',
      bgGradient: 'linear-gradient(135deg, rgba(255, 250, 245, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
      borderColor: '#ea580c'
    },
    {
      id: 'risky',
      label: 'Risky Path',
      icon: Zap,
      color: '#db2777', // Darker Pink for Light Mode
      softColor: '#db2777',
      description: 'Bold moves with high potential rewards and significant uncertainty',
      bgGradient: 'linear-gradient(135deg, rgba(255, 250, 245, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
      borderColor: '#db2777'
    }
  ];

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{
        position: 'relative',
        fontFamily: "'Montserrat', sans-serif"
      }}
    >
      {/* Fixed background layer - GPU accelerated to prevent scroll flickering */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/bluewhite.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          zIndex: -1,
          transform: 'translateZ(0)',
          willChange: 'transform'
        }}
      />
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;900&display=swap');
          .wrapped-text-shadow {
            text-shadow: 2px 2px 0px rgba(0,0,0,0.2);
          }
          .card-hover-effect:hover {
            transform: translateY(-4px);
            box-shadow: 0 10px 30px -10px rgba(0,0,0,0.5);
          }
        `}
      </style>
      {/* Tsenta survey banner */}
      {tsentaSurveyVisible && (
        <div style={{
          position: 'fixed', top: 72, left: 0, right: 0, zIndex: 49,
          display: 'flex', justifyContent: 'center', padding: '0 16px', pointerEvents: 'none',
        }}>
          <div style={{
            pointerEvents: 'auto',
            background: '#fff',
            borderRadius: 16,
            boxShadow: '0 4px 24px rgba(56, 189, 248, 0.18)',
            border: '2px solid #38bdf8',
            padding: '16px 20px',
            maxWidth: 420,
            width: '100%',
          }}>
            {tsentaSurveySubmitted ? (
              <p style={{ fontSize: 13, color: '#0ea5e9', fontWeight: 700, textAlign: 'center', margin: 0 }}>
                Thanks for the feedback!
              </p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, color: '#1c1c1e', margin: 0 }}>
                    How was your experience with Tsenta?
                  </p>
                  <button onClick={() => setTsentaSurveyVisible(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8E8E93', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { label: 'Loved it, I applied to jobs', rating: 1 },
                    { label: 'Good experience, still exploring', rating: 2 },
                    { label: 'It was OK, not sure yet', rating: 3 },
                    { label: "Didn't really use it", rating: 4 },
                    { label: "Didn't like it, came back to Fig", rating: 5 },
                  ].map(({ label, rating }) => (
                    <button
                      key={label}
                      onClick={() => handleTsentaSurvey(rating)}
                      style={{
                        background: '#f0f9ff', border: '1px solid #bae6fd',
                        borderRadius: 10, padding: '9px 14px', fontSize: 13,
                        fontFamily: 'inherit', cursor: 'pointer', color: '#0c4a6e',
                        fontWeight: 500, transition: 'all 0.15s', textAlign: 'left',
                        width: '100%',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#0ea5e9'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#0ea5e9'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#f0f9ff'; e.currentTarget.style.color = '#0c4a6e'; e.currentTarget.style.borderColor = '#bae6fd'; }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{
        borderColor: 'rgba(255, 255, 255, 0.3)',
        background: 'rgba(255, 252, 248, 0.65)',
        boxShadow: '0 4px 30px rgba(0, 0, 0, 0.08)',
        transform: 'translateZ(0)',
        WebkitBackdropFilter: 'blur(20px)',
        backdropFilter: 'blur(20px)'
      }}>
        <div className="w-full max-w-[1400px] mx-auto px-3 py-3 sm:px-4 sm:py-4 md:px-5 lg:px-6 xl:px-8">
          <div className="flex items-center justify-between">
            {/* Left — Logo */}
            <div className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_path-explorer-12/artifacts/cyj4b8vo_Fig%20Icon%202.png"
                alt="Fig Logo"
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg sm:text-xl hidden sm:block" style={{
                fontFamily: "'Chango', cursive",
                fontWeight: 400,
                color: '#2d1b0e',
                letterSpacing: '0.04em'
              }}>
                Fig Careers
              </h1>
            </div>

            {/* Center — Spill Tea Button + NEW FEATURE blurb */}
            <div className="relative flex flex-col items-center">
              <Button
                ref={spillTeaButtonRef}
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (user) {
                    const { count } = await supabase
                      .from('spill_tea_reviews')
                      .select('id', { count: 'exact' })
                      .eq('user_id', user.id);
                    if (count && count > 0) {
                      navigate('/spill-tea/home');
                    } else {
                      navigate('/spill-tea');
                    }
                  } else {
                    navigate('/spill-tea');
                  }
                }}
                className="gap-1.5"
                style={{
                  borderColor: 'rgba(37, 99, 235, 0.4)',
                  color: '#2563eb',
                  background: 'linear-gradient(135deg, rgba(245, 250, 255, 0.8), rgba(232, 243, 255, 0.6))',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '600',
                  fontSize: '0.8rem'
                }}
              >
                <span style={{ fontSize: '1rem' }}>🧋</span>
                <span className="hidden sm:inline">Spill Tea</span>
                <span className="sm:hidden">Tea</span>
              </Button>

              {/* NEW FEATURE tooltip blurb */}
              {showSpillTeaBlurb && (
                <div
                  className="absolute top-full mt-2 z-50"
                  style={{
                    animation: 'spillTeaBlurbIn 0.4s ease-out forwards, spillTeaBlurbOut 0.6s ease-in 3.8s forwards',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      background: 'linear-gradient(135deg, #1e3a5f, #2563eb)',
                      color: '#fff',
                      fontFamily: "'Inter', sans-serif",
                      fontSize: '0.72rem',
                      padding: '10px 18px',
                      borderRadius: '12px',
                      boxShadow: '0 6px 24px rgba(37, 99, 235, 0.35), 0 2px 8px rgba(0,0,0,0.12)',
                      letterSpacing: '0.02em',
                      lineHeight: '1.4',
                      textAlign: 'center',
                      width: '240px',
                    }}
                  >
                    {/* Little triangle pointer */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 0,
                        height: 0,
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderBottom: '7px solid #1e3a5f',
                      }}
                    />
                    <span style={{ fontSize: '0.65rem', fontWeight: '700', opacity: 0.9, display: 'block', marginBottom: '3px', letterSpacing: '0.08em' }}>NEW FEATURE</span>
                    <span style={{ fontWeight: '400' }}>Large database of internship reviews</span>
                  </div>
                </div>
              )}
            </div>

            {/* Right — Action buttons */}
            <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (!hasLifetimeAccess) { setShowPaywallModal(true); return; } navigate('/discover'); }}
                className="gap-2"
                style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <Search className="w-4 h-4" />
                <span className="hidden lg:inline xl:hidden">Discover</span>
                <span className="hidden xl:inline">Discover Gems</span>
              </Button>

              <Dialog open={isChatOpen} onOpenChange={(open) => { if (open && !hasLifetimeAccess) { setShowPaywallModal(true); return; } setIsChatOpen(open); }}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    style={{
                      borderColor: 'rgba(139, 92, 246, 0.5)',
                      color: '#7c3aed',
                      background: 'rgba(255, 250, 245, 0.6)',
                      fontFamily: "'Inter', sans-serif"
                    }}
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span className="hidden lg:inline">Fig Chat</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl p-0 bg-transparent border-none shadow-none text-left">
                  <div className="h-[780px] w-full">
                    <CoachTabContent formData={formData} />
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (!hasLifetimeAccess) { setShowPaywallModal(true); return; } handleExportPDF(); }}
                disabled={exporting}
                className="gap-2"
                style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <Download className="w-4 h-4" />
                <span className="hidden lg:inline xl:hidden">Export</span>
                <span className="hidden xl:inline">{exporting ? 'Exporting...' : 'Export PDF'}</span>
              </Button>

              {/* Global Jobs button — commented out
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/global-jobs')}
                className="gap-2"
                style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <Globe className="w-4 h-4" />
                <span className="hidden lg:inline">Global Jobs</span>
              </Button>
              */}

              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (!hasLifetimeAccess) { setShowPaywallModal(true); return; } window.open('https://figcareer.featurebase.app/', '_blank'); }}
                className="gap-2"
                style={{
                  borderColor: 'rgba(45, 27, 14, 0.2)',
                  color: '#2d1b0e',
                  background: 'rgba(255, 250, 245, 0.6)',
                  fontFamily: "'Inter', sans-serif"
                }}
              >
                <Lightbulb className="w-4 h-4" />
                <span className="hidden lg:inline">Feedback</span>
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://buymeacoffee.com/figcareer', '_blank')}
                className="gap-2 hidden lg:flex"
                style={{
                  borderColor: '#FFDD00',
                  color: '#000000',
                  background: '#FFDD00',
                  fontFamily: "'Inter', sans-serif",
                  fontWeight: '600'
                }}
              >
                <Coffee className="w-4 h-4" />
                <span className="hidden xl:inline">Support Us</span>
              </Button>
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2" style={{
                      borderColor: 'rgba(45, 27, 14, 0.2)',
                      color: '#2d1b0e',
                      background: 'rgba(255, 250, 245, 0.6)',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <User className="w-4 h-4" />
                      <span className="hidden lg:inline-block xl:hidden max-w-[120px] truncate align-bottom">Account</span>
                      <span className="hidden xl:inline-block max-w-[180px] truncate align-bottom">{user.email}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => { if (!hasLifetimeAccess) { setShowPaywallModal(true); return; } navigate('/saved'); }}>
                      <History className="w-4 h-4 mr-2" />
                      Saved Roadmaps & Careers
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={(e) => { e.preventDefault(); if (!hasLifetimeAccess) { setShowPaywallModal(true); return; } setShowResumeDialog(true); }}>
                      <FileText className="w-4 h-4 mr-2" />
                      Replace Resume
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {hasLifetimeAccess ? (
                      <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium" style={{ color: '#007AFF' }}>
                        <Crown className="w-4 h-4" />
                        Founding Member
                      </div>
                    ) : (
                      <>
                        <DropdownMenuItem onClick={handleUpgradeClick}>
                          <Crown className="w-4 h-4 mr-2" />
                          Upgrade Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleManageBilling}>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Manage Billing
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowDeleteConfirm(true)}
                      className="text-red-500 focus:text-red-600 focus:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAuthModal(true)}
                  style={{
                    borderColor: 'rgba(45, 27, 14, 0.2)',
                    color: '#2d1b0e',
                    background: 'rgba(255, 250, 245, 0.6)',
                    fontFamily: "'Inter', sans-serif"
                  }}
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full sm:container sm:mx-auto px-0 sm:px-6 lg:px-8 py-0 sm:py-12 overflow-x-hidden" style={{ minHeight: '100vh', position: 'relative', zIndex: 1 }}>
        {/* Orbit System Summary */}
        <div className="max-w-7xl mx-auto mt-24 mb-16 flex justify-center">
          <OrbitSystem
            candidateName={formData?.resume_data?.name || user?.user_metadata?.full_name || (user?.email ? user.email.split('@')[0] : null)}
            innerIcons={innerIcons}
            outerIcons={outerIcons}
          />
        </div>

        {/* Career News Section */}
        <div className="max-w-7xl mx-auto mb-16">
          <CareerNewsCarousel
            resumeData={formData?.resume_data}
            interests={formData?.resume_data?.industries || []}
          />
        </div>

                {/* Build a Personal Brand Banner - Glassy Window Preview */}
        <div className="max-w-4xl mx-auto mb-16 w-[calc(100%-1rem)] sm:w-full">
          <div
            className="group relative overflow-hidden rounded-3xl sm:rounded-[32px] border border-white/40 transition-all duration-500 hover:shadow-[0_12px_40px_-12px_rgba(255,255,255,0.4)]"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.25) 100%)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.12),
                0 2px 8px rgba(0, 0, 0, 0.08),
                inset 0 1px 0 rgba(255, 255, 255, 0.6),
                inset 0 -1px 0 rgba(255, 255, 255, 0.2)
              `,
              backdropFilter: 'blur(24px) saturate(180%)',
              WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            }}
          >
            {/* Graphical Background Enhancements */}
            <div className="absolute top-0 right-0 w-80 h-80 bg-white/20 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/4 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/10 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/4 pointer-events-none" />
            <div className="absolute top-0 left-0 right-0 h-1/4 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />

            <div className="relative px-6 sm:px-10 py-8 sm:py-10">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 md:gap-12">
                <div className="w-full md:max-w-lg lg:max-w-xl z-10">
                  <h3
                    className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight drop-shadow-md"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Build a personal brand
                  </h3>
                  
                  <p 
                    className="text-white/95 text-base sm:text-lg leading-relaxed font-medium mb-8"
                    style={{ fontFamily: "'Inter', sans-serif", textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}
                  >
                    Fig's creative writing tool helps you engaging posts and articles that get you noticed
                  </p>
                  
                  <div
                    onClick={async () => {
                      if (user) {
                        try {
                          await supabase.from('creative_writing_agent_clicks').insert({
                            user_id: user.id,
                            user_email: user.email || null
                          });
                        } catch (error) {
                          console.error('Failed to track creative writing agent click:', error);
                        }
                      } else {
                        try {
                          await supabase.from('creative_writing_agent_clicks').insert({
                            user_email: null
                          });
                        } catch (error) {}
                      }
                      navigate('/writing');
                    }}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all hover:scale-105 bg-white text-[#1a1a1a] cursor-pointer group/btn"
                    style={{
                      fontFamily: "'Montserrat', sans-serif",
                      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15), inset 0 -2px 0 rgba(0,0,0,0.05)'
                    }}
                  >
                    <span>Try it out now!</span>
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </div>
                </div>

                {/* Right side interactive/visual representation */}
                <div className="hidden md:flex flex-col gap-4 w-full max-w-[280px] shrink-0 relative z-10 perspective-1000">
                  <div 
                    className="bg-white/40 backdrop-blur-xl border border-white/50 rounded-2xl p-5 transform hover:-translate-y-2 hover:rotate-1 transition-all duration-300 shadow-[0_8px_24px_rgba(0,0,0,0.1)]"
                    style={{ transformOrigin: 'bottom right', transform: 'rotate(-2deg)' }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-slate-200 border-2 border-white flex items-center justify-center text-xs">👤</div>
                      <div>
                        <div className="h-2 w-16 bg-white/80 rounded-full mb-1.5"></div>
                        <div className="h-1.5 w-10 bg-white/50 rounded-full"></div>
                      </div>
                    </div>
                    <div className="h-2 w-full bg-white/70 rounded-full mb-2"></div>
                    <div className="h-2 w-5/6 bg-white/70 rounded-full mb-2"></div>
                    <div className="h-2 w-4/6 bg-white/70 rounded-full"></div>
                  </div>
                  
                  <div 
                    className="bg-white/20 backdrop-blur-md border border-white/30 rounded-2xl p-4 transform hover:-translate-y-1 hover:-rotate-1 transition-all duration-300 translate-x-6 -mt-8 shadow-[0_8px_24px_rgba(0,0,0,0.08)]"
                    style={{ transformOrigin: 'top left', transform: 'rotate(3deg)' }}
                  >
                    <div className="h-2 w-1/4 bg-white/60 rounded-full mb-3"></div>
                    <div className="h-1.5 w-full bg-white/40 rounded-full mb-2"></div>
                    <div className="h-1.5 w-11/12 bg-white/40 rounded-full mb-2"></div>
                    <div className="h-1.5 w-3/4 bg-white/40 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

{/* Intro Section */}
        <div className="max-w-4xl mx-auto mb-12 sm:mb-16 bg-black/70 sm:rounded-3xl p-6 sm:p-8 border-y sm:border border-white/10" style={{ transform: 'translateZ(0)' }}>
          <div className="text-center space-y-6">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase wrapped-text-shadow" style={{
              fontFamily: "'Montserrat', sans-serif",
              color: '#FFFFFF',
              letterSpacing: '-0.02em',
              lineHeight: '0.9'
            }}>
              Your Career Roadmaps
            </h2>
            <p className="text-base sm:text-lg max-w-2xl mx-auto font-medium mb-4" style={{
              fontFamily: "'Montserrat', sans-serif",
              color: 'rgba(255, 255, 255, 0.9)',
              lineHeight: '1.6'
            }}>
              We've created three personalized career paths based on your profile. Each path represents
              a different approach to achieving your career goals.
            </p>
          </div>


          {/* Edit Roadmap Input - Hidden for now */}
          <div className="max-w-800px mx-auto mb-8" style={{ maxWidth: '800px', display: 'none' }}>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20 flex flex-col sm:flex-row gap-4">
              <Input
                value={editInstruction}
                onChange={(e) => setEditInstruction(e.target.value)}
                placeholder="Want to tweak this? Type instructions (e.g., 'Make it more focused on startups')..."
                className="bg-black/20 border-none text-white placeholder:text-white/50 h-12"
                disabled={isEditing}
              />
              <Button
                onClick={handleEditRoadmap}
                disabled={isEditing || !editInstruction.trim()}
                className="bg-white text-black hover:bg-gray-200 font-bold whitespace-nowrap h-12 px-6"
              >
                {isEditing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                {isEditing ? 'Updating...' : 'Edit Roadmap'}
              </Button>
            </div>
          </div>

          {/* Path Tabs */}
          <div className="max-w-4xl mx-auto">
            {/* Filter available levels based on generated data */}
            {(() => {
              // Ensure riskLevels are only fully available if they exist in careerPaths
              // But we want to render the tabs anyway if we are "waiting" for stable to be set
              const availableLevels = riskLevels;

              // If NO data at all yet (loading initial state)
              if (!careerPaths) {
                if (researching) {
                  return (
                    <div className="w-full py-24 flex flex-col items-center justify-center bg-white/90 rounded-3xl border border-white/20" style={{ transform: 'translateZ(0)' }}>
                      <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mb-4" />
                      <h3 className="text-xl font-bold text-slate-800">Architecting your career...</h3>
                      <p className="text-slate-600">Analyzing stable pathways first</p>
                    </div>
                  );
                }
                return null;
              }

              const gridCols = 'grid-cols-3';

              // Helper function to render a path card
              const renderPathCard = (level) => {
                const isLocked = level.id !== 'stable' && !isStableSet && !careerPaths?.adventurous && !careerPaths?.risky;

                return (
                  <Card key={level.id} className="rounded-3xl sm:rounded-[2.5rem] w-[calc(100%-1rem)] mx-auto sm:w-full !mb-12 transition-all duration-500 overflow-hidden" style={{
                    background: '#FFFFFF',
                    border: `1px solid rgba(0,0,0,0.05)`,
                    borderTop: `4px solid ${level.borderColor}`,
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1), 0 0 1px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent className="p-5 sm:p-8 lg:p-12 relative">
                      {/* Ambient background glow matching the level color */}
                      <div className="absolute top-0 left-0 w-full h-40 opacity-5 pointer-events-none" style={{
                        background: `radial-gradient(ellipse at 50% 0%, ${level.borderColor} 0%, transparent 70%)`
                      }} />

                      {/* Path Description */}
                      <div className="mb-6 sm:mb-10 relative z-10 text-center sm:text-left flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                        <div
                          className="w-14 h-14 sm:w-16 sm:h-16 rounded-[1rem] sm:rounded-2xl flex items-center justify-center flex-shrink-0 relative overflow-hidden"
                          style={{
                            background: `linear-gradient(135deg, ${level.borderColor}15, ${level.borderColor}05)`,
                            border: `1px solid ${level.borderColor}30`,
                            boxShadow: `inset 0 1px 2px rgba(255,255,255,0.5)`,
                          }}
                        >
                          <level.icon className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: level.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl sm:text-3xl font-black mb-1 sm:mb-2 tracking-tight" style={{
                            fontFamily: "'Montserrat', sans-serif",
                            color: '#1a1a1a',
                          }}>
                            {level.label}
                          </h3>
                          <p className="text-[13px] sm:text-base font-medium max-w-2xl text-center sm:text-left mx-auto sm:mx-0" style={{
                            fontFamily: "'Inter', sans-serif",
                            color: '#555',
                            lineHeight: '1.6'
                          }}>
                            {level.description}
                          </p>
                        </div>
                      </div>

                      {/* VISUALIZATION OR LOCKED STATE */}
                      {isLocked ? (
                        <div className="py-20 flex flex-col items-center justify-center text-center">
                          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                            <Shield className="w-8 h-8 text-slate-400" />
                          </div>
                          <h4 className="text-xl font-bold text-slate-700 mb-2">
                            {level.label} is Loading...
                          </h4>
                          <p className="text-slate-500 max-w-md mx-auto">
                            Please fully build and "Set" your Stable Roadmap first.
                            We will curate this path based on your stable choices.
                          </p>
                        </div>
                      ) : (
                        /* Roadmap Visualization */
                        <RoadmapVisualization
                          path={careerPaths?.[level.id]}
                          color={level.id === 'stable' ? '#16a34a' : level.id === 'adventurous' ? '#ea580c' : '#db2777'}
                          riskLevel={level.id}
                          roadmapId={roadmapId || currentRoadmapId}
                          formData={formData}
                          onRetry={() => generateCareerPathsWithAI(formData)}
                          isGenerating={researching}
                          onDislikeRole={handleDislikeRole}
                          savedRoles={savedRoles}
                          onSaveRole={handleSaveRole}
                          whatIfOverrides={careerPaths?.[level.id]?.whatIfOverrides}
                          whatIfActiveNode={activeTab === level.id ? whatIfActiveNode : null}
                          whatIfLoading={whatIfLoading}
                          onWhatIfDrop={handleWhatIfDrop}
                          onWhatIfSubmit={(nodeIndex, question) => handleWhatIfSubmit(nodeIndex, question)}
                          onWhatIfReset={handleWhatIfReset}
                          onWhatIfClose={handleWhatIfClose}
                          hasLifetimeAccess={hasLifetimeAccess}
                          onPaywallRequired={() => setShowPaywallModal(true)}
                          onApplyWithTsenta={handleApplyWithTsenta}
                          onSetRoadmap={async (newNodes, isFinal = false) => {
                            if (level.id === 'stable') {
                              // Handle updates (Set or Reset)
                              if (!newNodes) {
                                // Legacy fallback just in case
                                setIsStableSet(true);
                                sessionStorage.setItem('roadmap_isStableSet', 'true');
                                return;
                              }

                              // Only mark as complete/locked if explicitly finalized by user
                              const isComplete = isFinal && newNodes.length >= 4;
                              setIsStableSet(isComplete);
                              sessionStorage.setItem('roadmap_isStableSet', isComplete ? 'true' : 'false');

                              // Show shareable card immediately when complete
                              if (isComplete) {
                                setStablePathNodes(newNodes);
                                setShowShareableCard(true);
                              }

                              // Update local state deeply
                              const updatedStable = {
                                ...careerPaths.stable,
                                nodes: newNodes,
                                selected_path: newNodes, // Save user selection explicitly
                                path_type: 'stable',
                                locked: isComplete, // Persist locked status
                                saved_at: new Date().toISOString()
                              };

                              // Build updated paths object
                              let newPaths = { ...careerPaths, stable: updatedStable };

                              if (isComplete) {
                                // Trigger AI generation of adventurous + risky, informed by the stable choices
                                setCareerPaths(newPaths); // Render stable immediately while secondary paths load
                                try {
                                  const { data: { session: _secSession } } = await supabase.auth.getSession();
                                  const secRes = await fetch(`${BACKEND_URL}/api/generate-secondary-paths`, {
                                    method: 'POST',
                                    headers: {
                                      'Content-Type': 'application/json',
                                      'Authorization': `Bearer ${_secSession?.access_token}`,
                                    },
                                    body: JSON.stringify({
                                      stable_nodes: newNodes,
                                      user_context: formData,
                                    }),
                                  });
                                  if (secRes.ok) {
                                    const secData = await secRes.json();
                                    newPaths = {
                                      ...newPaths,
                                      adventurous: { ...secData.adventurous, path_type: 'adventurous', generated_at: new Date().toISOString() },
                                      risky: { ...secData.risky, path_type: 'risky', generated_at: new Date().toISOString() },
                                    };
                                  } else {
                                    // Fallback to local generators if backend fails
                                    newPaths.adventurous = { ...generateAdventurousPath(newNodes), path_type: 'adventurous' };
                                    newPaths.risky = { ...generateRiskyPath(newNodes), path_type: 'risky' };
                                  }
                                } catch {
                                  // Fallback to local generators on network error
                                  newPaths.adventurous = { ...generateAdventurousPath(newNodes), path_type: 'adventurous' };
                                  newPaths.risky = { ...generateRiskyPath(newNodes), path_type: 'risky' };
                                }
                              }

                              setCareerPaths(newPaths);

                              // Update Supabase
                              const targetId = roadmapId || currentRoadmapId;

                              if (targetId) {
                                try {
                                  const { data, error } = await supabase
                                    .from('career_roadmaps')
                                    .update({
                                      career_paths: newPaths,
                                      updated_at: new Date().toISOString()
                                    })
                                    .eq('id', targetId);

                                  if (error) throw error;

                                  // If complete, show shareable card (no auto-scroll)
                                  if (isComplete) {
                                    setStablePathNodes(newNodes);
                                    setShowShareableCard(true);
                                  }
                                } catch (err) {
                                  console.error("Failed to auto-save roadmap:", err);
                                  toast.error("Failed to save changes");
                                }
                              } else {
                                console.warn("⚠️ No roadmap ID available for saving. roadmapId:", roadmapId, "currentRoadmapId:", currentRoadmapId);
                              }
                            }
                          }}
                        />
                      )}

                    </CardContent>
                  </Card>
                );
              };

              return (
                <div className="w-full" ref={pathTabsRef} style={{ scrollMarginTop: '100px' }}>
                  {/* Path Selection Buttons */}
                  <Tabs value={activeTab} onValueChange={(value) => { if (!hasLifetimeAccess && value !== 'stable') { setShowPaywallModal(true); return; } setActiveTab(value); sessionStorage.setItem('roadmap_activeTab', value); trackPathSelected(value); }} className="w-full">
                    {/* Glassy Segmented Control (Pill) container */}
                    <div className="flex justify-center mb-8 sm:mb-12 mt-6 sm:mt-10 px-1 sm:px-0">
                      <TabsList className="inline-flex w-full sm:w-auto p-1.5 sm:p-2 bg-white/5 backdrop-blur-xl rounded-[1.25rem] sm:rounded-full border border-white/10 shadow-2xl relative z-10 gap-1 sm:gap-2 h-auto" style={{
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 1px rgba(255,255,255,0.05)'
                      }}>
                        {availableLevels.map((level) => {
                          const isLocked = level.id !== 'stable' && !isStableSet && !careerPaths?.adventurous && !careerPaths?.risky;
                          const path = careerPaths?.[level.id];
                          const isActive = activeTab === level.id;

                          return (
                            <TabsTrigger
                              key={level.id}
                              value={level.id}
                              disabled={isLocked}
                              className={`
                                flex flex-1 sm:flex-initial flex-col sm:flex-row items-center justify-center 
                                gap-1.5 sm:gap-2.5 py-3 px-2 sm:py-3.5 sm:px-8 
                                rounded-xl sm:rounded-full transition-all duration-500 relative
                                ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-white/5'}
                              `}
                              style={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: isActive ? 900 : 700,
                                color: isActive ? '#1a1a1a' : 'rgba(255, 255, 255, 0.65)',
                                background: isActive ? '#FFFFFF' : 'transparent',
                                opacity: isLocked ? 0.4 : 1,
                                filter: isLocked ? 'grayscale(80%)' : 'none',
                                boxShadow: isActive 
                                  ? `0 4px 16px -4px ${level.borderColor}60, 0 1px 3px rgba(0,0,0,0.1)` 
                                  : 'none',
                                transform: isActive ? 'scale(1)' : 'scale(0.98)'
                              }}
                            >
                              <level.icon 
                                className="w-5 h-5 sm:w-5 sm:h-5 transition-transform duration-500" 
                                style={{ 
                                  color: isActive ? level.color : 'rgba(255, 255, 255, 0.4)',
                                  transform: isActive ? 'scale(1.1) translateY(-1px)' : 'scale(1)'
                                }} 
                              />
                              <span className="text-[10px] sm:text-sm tracking-widest uppercase text-center">
                                {/* Mobile: "Stable" or two lines. Desktop: "Stable Path" */}
                                <span className="inline">{level.label.split(' ')[0]}</span>
                                <span className="hidden sm:inline"> {level.label.split(' ')[1]}</span>
                              </span>
                              
                              {/* Subtle LOCK badge */}
                              {isLocked && path?.nodes && (
                                <div className="absolute top-1 right-1 sm:-top-1 sm:-right-2 flex items-center justify-center">
                                  <div className="bg-slate-800 text-white text-[7px] sm:text-[9px] px-1.5 py-0.5 rounded-full font-bold shadow-lg border border-slate-600 tracking-wider">
                                    LOCKED
                                  </div>
                                </div>
                              )}
                            </TabsTrigger>
                        );
                      })}
                      </TabsList>
                    </div>
                    {!isStableSet && !careerPaths?.adventurous && !careerPaths?.risky && (
                      <p className="text-center text-xs text-white/40 tracking-wide -mt-6 mb-8">
                        🔒 Complete your Stable Path to unlock the other two
                      </p>
                    )}
                  </Tabs>

                  {/* Show only the active tab's path (replaces stable when switching) */}
                  {renderPathCard(availableLevels.find(l => l.id === activeTab))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Community Feed Banner - Glassy Window Preview */}
        <div className="max-w-4xl mx-auto mb-16 w-[calc(100%-1rem)] sm:w-full">
          <div
            onClick={() => hasLifetimeAccess ? navigate('/feed') : setShowPaywallModal(true)}
            className="cursor-pointer group relative overflow-hidden rounded-3xl sm:rounded-[32px] border border-white/40 transition-all duration-500 hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0.2) 100%)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.12),
                0 2px 8px rgba(0, 0, 0, 0.08),
                inset 0 1px 0 rgba(255, 255, 255, 0.6),
                inset 0 -1px 0 rgba(255, 255, 255, 0.2)
              `,
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            }}
          >
            {/* Glass reflection effects */}
            <div className="absolute top-0 left-0 right-0 h-1/3 bg-gradient-to-b from-white/30 to-transparent rounded-t-[32px] pointer-events-none" />
            <div className="absolute top-3 left-8 w-32 h-6 bg-white/20 rounded-full blur-lg transform -rotate-6" />

            {/* Header with title */}
            <div className="relative px-6 pt-5 pb-4 border-b border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <h3
                    className="text-xl md:text-2xl font-black text-white mb-0.5 tracking-tight drop-shadow-sm"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Community Feed
                  </h3>
                  <p
                    className="text-white/70 text-sm font-medium"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    see what others are saying about work, careers, life, and more
                  </p>
                </div>
                <div
                  className="hidden sm:flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-all group-hover:scale-105 group-hover:bg-white/95"
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    color: '#1a1a1a',
                    fontFamily: "'Montserrat', sans-serif",
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  <span>Explore</span>
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </div>

            {/* Preview posts - glimpse through the window (2.5 posts visible) */}
            <div className="relative px-4 py-4 space-y-3 overflow-hidden" style={{ maxHeight: '240px' }}>
              {feedPreviewPosts.length > 0 ? (
                <>
                  {feedPreviewPosts.slice(0, 3).map((post, index) => (
                    <div
                      key={post.id}
                      className="p-4 rounded-xl transition-all"
                      style={{
                        background: index === 0 ? 'rgba(255, 255, 255, 0.75)' : index === 1 ? 'rgba(255, 255, 255, 0.6)' : 'rgba(255, 255, 255, 0.45)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      <p className={`text-sm leading-relaxed line-clamp-2 ${index === 0 ? 'text-slate-700' : index === 1 ? 'text-slate-600' : 'text-slate-500'}`} style={{ fontFamily: "'Inter', sans-serif" }}>
                        {post.content}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                        <span className="flex items-center gap-1">↑ {post.score || 0}</span>
                        <span className="flex items-center gap-1">💬 {post.comment_count || 0}</span>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                /* Fallback sample posts if API fails */
                <>
                  <div
                    className="p-4 rounded-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.75)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <p className="text-slate-700 text-sm leading-relaxed line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Just got promoted to senior engineer after 2 years. The secret? I stopped trying to impress everyone and started focusing on shipping quality work consistently. 🚀
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span>↑ 47</span>
                      <span>💬 12</span>
                    </div>
                  </div>
                  <div
                    className="p-4 rounded-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.6)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.5)'
                    }}
                  >
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Hot take: Networking isn't about collecting LinkedIn connections. It's about having genuine conversations with interesting people...
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      <span>↑ 89</span>
                      <span>💬 34</span>
                    </div>
                  </div>
                  <div
                    className="p-4 rounded-xl"
                    style={{
                      background: 'rgba(255, 255, 255, 0.45)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255, 255, 255, 0.4)'
                    }}
                  >
                    <p className="text-slate-500 text-sm leading-relaxed line-clamp-2" style={{ fontFamily: "'Inter', sans-serif" }}>
                      Anyone else feel like imposter syndrome never fully goes away? Even after 5 years in tech...
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>↑ 156</span>
                      <span>💬 67</span>
                    </div>
                  </div>
                </>
              )}

              {/* Fade overlay to cut off the third post and suggest more content */}
              <div
                className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none"
                style={{
                  background: 'linear-gradient(to top, rgba(200,210,220,0.5) 0%, rgba(200,210,220,0.3) 40%, transparent 100%)'
                }}
              />
            </div>

            {/* Mobile explore button */}
            <div className="sm:hidden px-4 pb-4">
              <div
                className="flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm w-full"
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#1a1a1a',
                  fontFamily: "'Montserrat', sans-serif",
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}
              >
                <span>Explore Feed</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </div>
          </div>
        </div>

      </main>

      {/* Shareable Card Section - Outside roadmap container */}
      {showShareableCard && stablePathNodes && (
        <div id="shareable-card-section" className="w-full py-8">
          <div className="w-full sm:container sm:mx-auto px-4 sm:px-6 lg:px-8">
            <ShareableCard
              selectedPath="stable"
              careerOptions={stablePathNodes}
              roadmapId={roadmapId || currentRoadmapId}
              formData={formData}
              userName={user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Explorer'}
            />
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-10 mt-16 w-full" style={{
        borderTop: '1px solid rgba(45, 27, 14, 0.1)',
        background: 'rgba(255, 250, 245, 0.95)',
        transform: 'translateZ(0)'
      }}>
        <div className="w-full sm:container sm:mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm" style={{
            fontFamily: "'Inter', sans-serif",
            color: 'rgba(45, 27, 14, 0.6)',
            lineHeight: '1.6'
          }}>
            <p>© 2026 Fig Careers. Remember, it's ok to take a silly little detour sometimes - Magan & Bhavya</p>
          </div>
        </div>
      </footer>

      <BookmarkFlyAnimation
        show={bookmarkAnim.show}
        x={bookmarkAnim.x}
        y={bookmarkAnim.y}
        onComplete={() => setBookmarkAnim({ show: false, x: 0, y: 0 })}
      />

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />

      {/* Stripe Checkout Sidebar */}
      {showCheckoutSidebar && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-50 transition-opacity"
            onClick={handleCloseSidebar}
            style={{ backdropFilter: 'blur(4px)' }}
          />

          {/* Sidebar */}
          <div
            className="fixed top-0 right-0 h-full w-full sm:w-[500px] z-50 shadow-2xl transition-transform duration-300 ease-in-out"
            style={{
              background: 'rgba(255, 250, 245, 0.98)',
              backdropFilter: 'blur(12px)',
              borderLeft: '1px solid rgba(45, 27, 14, 0.1)',
              fontFamily: "'Inter', sans-serif",
              transform: showCheckoutSidebar ? 'translateX(0)' : 'translateX(100%)',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'rgba(45, 27, 14, 0.1)' }}>
                <h2 className="text-2xl font-bold" style={{ color: '#2d1b0e' }}>
                  {checkoutClientSecret ? 'Complete Payment' : 'Choose Your Plan'}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCloseSidebar}
                  className="rounded-full"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {!checkoutClientSecret ? (
                  /* Stripe Pricing Table - Shows plans directly from Stripe */
                  <div className="w-full">
                    <stripe-pricing-table
                      pricing-table-id={process.env.REACT_APP_STRIPE_PRICING_TABLE_ID}
                      publishable-key={process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}
                      customer-email={user?.email}
                      ref={pricingTableRef}
                    />
                  </div>
                ) : (
                  /* Stripe Embedded Checkout - Shows after plan selection */
                  <div className="h-full">
                    <div id="checkout" ref={checkoutRef} className="w-full" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={showResumeDialog} onOpenChange={setShowResumeDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Resume</DialogTitle>
          </DialogHeader>
          <ReplaceResumeButton onResumeReplaced={(newResumeData) => {
            setShowResumeDialog(false);
            if (newResumeData && formData) {
              const { filename, uploaded_at, ...extractedData } = newResumeData;
              const updatedFormData = { ...formData, resume_data: extractedData };
              setFormData(updatedFormData);
              generateCareerPathsWithAI(updatedFormData, currentRoadmapId);
            }
          }} />
        </DialogContent>
      </Dialog>

      {/* Inline paywall modal */}
      <PaywallModal
        isOpen={showPaywallModal}
        onClose={() => setShowPaywallModal(false)}
        onSuccess={() => { grantLifetimeAccess(); setShowPaywallModal(false); }}
      />

      {/* Delete Account confirmation modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowDeleteConfirm(false); }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Delete your account?</h2>
            <p className="text-sm text-gray-500 mb-6">
              This will permanently delete your account, roadmaps, and all saved data. This action cannot be undone.
            </p>
            <button
              onClick={handleDeleteAccount}
              disabled={isDeletingAccount}
              className="w-full py-3 rounded-xl text-white font-semibold text-base mb-3 transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#ef4444' }}
              onMouseEnter={e => { if (!isDeletingAccount) e.currentTarget.style.backgroundColor = '#dc2626'; }}
              onMouseLeave={e => { if (!isDeletingAccount) e.currentTarget.style.backgroundColor = '#ef4444'; }}
            >
              {isDeletingAccount ? 'Deleting...' : 'Yes, delete my account'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeletingAccount}
              className="w-full py-3 rounded-xl text-gray-600 font-medium text-base border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Post-payment activation spinner */}
      {isPollingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-10 flex flex-col items-center text-center max-w-sm w-full mx-4">
            <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mb-5" style={{ borderColor: '#007AFF', borderTopColor: 'transparent' }} />
            <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>Setting up your access...</h2>
            <p className="text-sm text-gray-500">Your payment was successful. Hang tight for just a second.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoadmapPage;
